import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  getMetadata,
  deleteObject,
} from "firebase/storage";
import "./Reports.css";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import wdiPage1 from "../assets/wdi-page-1.png";
import wdiPage2 from "../assets/wdi-page-2.png";

// PDF.js worker.
// Do NOT use `new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url)` here.
// Create React App runs that .mjs file through Babel, which injects imports like
// "/Users/.../node_modules/@babel/runtime/helpers/esm/toArray.js". The browser
// requests those paths from the app's own domain, Hosting answers with index.html
// (MIME text/html), the worker dies, and the PDF never renders — so no fields show.
// Load the untouched, version-matched worker instead. To self-host later, copy
// node_modules/pdfjs-dist/build/pdf.worker.min.mjs into /public and set
// REACT_APP_PDF_WORKER_URL=/pdf.worker.min.mjs in .env.
pdfjs.GlobalWorkerOptions.workerSrc =
  process.env.REACT_APP_PDF_WORKER_URL ||
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MAX_TEMPLATE_PAGES = 50;

// Field x / y / width / height / fontSize are stored in "base" units: pixels on
// a page rendered 816px wide (100% zoom). Zoom only scales the display, so a
// template looks the same every time it is loaded, at any zoom, and the PDF
// output never depends on the zoom level you happened to be at.
const BASE_PAGE_WIDTH = 816;

const NUMERIC_FIELD_DEFAULTS = {
  page: 0,
  formOrder: 0,
  x: 0,
  y: 0,
  width: 180,
  height: 20,
  fontSize: 14,
  letterSpacing: 0,
  lineHeight: 1.15,
};

const REPORT_STEPS = [
  { step: 1, title: "Template", help: "Pick one or create a new one", color: "step-blue" },
  { step: 2, title: "Upload PDF", help: "The blank form you fill in", color: "step-purple" },
  { step: 3, title: "Place Fields", help: "What gets filled in, and where", color: "step-green" },
  { step: 4, title: "Fill Out Report", help: "Property, values, signatures", color: "step-orange" },
  { step: 5, title: "PDF & Saved Reports", help: "Generate, open, reopen", color: "step-teal" },
];

// Firestore rejects `undefined`, and a cleared number input produces NaN.
// Clean both before every template write so a save never silently fails.
function sanitizeTemplateForSave(template) {
  const rest = { ...(template || {}) };
  delete rest.id;

  const fields = (rest.fields || []).map((field) => {
    const clean = {};
    Object.entries(field || {}).forEach(([key, value]) => {
      if (value === undefined) return;
      if (key in NUMERIC_FIELD_DEFAULTS) {
        const n = value === "" || value === null ? NaN : Number(value);
        clean[key] = Number.isFinite(n) ? n : NUMERIC_FIELD_DEFAULTS[key];
        return;
      }
      clean[key] = value;
    });
    return clean;
  });

  const out = {};
  Object.entries({ ...rest, fields }).forEach(([key, value]) => {
    if (value !== undefined) out[key] = value;
  });
  return out;
}

// Word-wrap one paragraph so the PDF wraps the same way the on-screen box does.
function wrapLineForPdf(text, font, size, maxWidth, charSpacing = 0) {
  const measure = (t) =>
    font.widthOfTextAtSize(t, size) + Math.max(0, t.length - 1) * charSpacing;

  if (!text) return [""];
  if (!maxWidth || measure(text) <= maxWidth) return [text];

  const words = text.split(/(\s+)/);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const candidate = current + word;
    if (measure(candidate) <= maxWidth || !current.trim()) {
      current = candidate;
    } else {
      lines.push(current.trimEnd());
      current = word.trimStart();
    }
  });

  if (current) lines.push(current.trimEnd());
  return lines.length ? lines : [text];
}

// Priority seeds its built-in WDI Inspection Report the first time the page
// opens with no templates. Its pages are the bundled WDI page images (the
// original Priority template format: `pages: [{ imageUrl, ... }]`). Templates
// in that image-page format still render, fill and generate PDFs below; upload
// a PDF in Step 2 at any time to switch a template to the PDF format.
const DEFAULT_TEMPLATE = {
  key: "wdi",
  name: "WDI Inspection Report",
  templatePdfUrl: "",
  templatePdfStoragePath: "",
  templatePdfFileName: "",
  pageCount: 0,
  pages: [
    { id: "page1", imageUrl: wdiPage1, width: 816, height: 1056, storagePath: "", fileName: "" },
    { id: "page2", imageUrl: wdiPage2, width: 816, height: 1056, storagePath: "", fileName: "" },
  ],
  fields: [
    {
      id: "companyName",
      label: "Company Name",
      type: "text",
      page: 0,
      x: 12,
      y: 48,
      width: 330,
      height: 20,
      fontSize: 14,
    },
    {
      id: "companyAddress",
      label: "Company Address",
      type: "textarea",
      page: 0,
      x: 12,
      y: 74,
      width: 330,
      height: 38,
      fontSize: 13,
    },
    {
      id: "businessLicenseNumber",
      label: "Business License No.",
      type: "text",
      page: 0,
      x: 422,
      y: 48,
      width: 150,
      height: 20,
      fontSize: 14,
    },
    {
      id: "dateOfInspection",
      label: "Date of Inspection",
      type: "date",
      page: 0,
      x: 640,
      y: 48,
      width: 120,
      height: 20,
      fontSize: 14,
    },
    {
      id: "propertyAddress",
      label: "Property Address",
      type: "textarea",
      page: 0,
      x: 422,
      y: 83,
      width: 330,
      height: 40,
      fontSize: 13,
    },
    {
      id: "inspectorSignature",
      label: "Inspector Signature",
      type: "signature",
      page: 0,
      x: 18,
      y: 135,
      width: 90,
      height: 45,
    },
    {
      id: "inspectorPrintedName",
      label: "Inspector Name",
      type: "text",
      page: 0,
      x: 192,
      y: 143,
      width: 180,
      height: 20,
      fontSize: 13,
    },
    {
      id: "certificationNumber",
      label: "Certification Number",
      type: "text",
      page: 0,
      x: 395,
      y: 143,
      width: 120,
      height: 20,
      fontSize: 13,
    },
    {
      id: "structuresInspected",
      label: "Structures Inspected",
      type: "text",
      page: 0,
      x: 570,
      y: 143,
      width: 190,
      height: 20,
      fontSize: 13,
    },
    {
      id: "findingsA",
      label: "Findings A",
      type: "checkbox",
      page: 0,
      x: 9,
      y: 257,
      width: 18,
      height: 18,
    },
    {
      id: "findingsB",
      label: "Findings B",
      type: "checkbox",
      page: 0,
      x: 9,
      y: 286,
      width: 18,
      height: 18,
    },
    {
      id: "liveInsects",
      label: "Live Insects",
      type: "textarea",
      page: 0,
      x: 110,
      y: 316,
      width: 630,
      height: 30,
      fontSize: 13,
    },
    {
      id: "evidenceObserved",
      label: "Evidence Observed",
      type: "textarea",
      page: 0,
      x: 52,
      y: 360,
      width: 688,
      height: 45,
      fontSize: 13,
    },
    {
      id: "visibleDamage",
      label: "Visible Damage",
      type: "textarea",
      page: 0,
      x: 128,
      y: 418,
      width: 612,
      height: 32,
      fontSize: 13,
    },
    {
      id: "sectionVComments",
      label: "Additional Comments",
      type: "textarea",
      page: 0,
      x: 26,
      y: 834,
      width: 710,
      height: 72,
      fontSize: 13,
    },
    {
      id: "sellerSignature",
      label: "Seller Signature",
      type: "signature",
      page: 0,
      x: 18,
      y: 982,
      width: 120,
      height: 40,
    },
    {
      id: "sellerPrintedName",
      label: "Seller Printed Name",
      type: "text",
      page: 0,
      x: 14,
      y: 1068,
      width: 220,
      height: 18,
      fontSize: 12,
    },
    {
      id: "buyerSignature",
      label: "Buyer Signature",
      type: "signature",
      page: 0,
      x: 430,
      y: 982,
      width: 120,
      height: 40,
    },
  ],
};

// Image pages from a legacy (pre-PDF) Priority template. Only used when the
// template has no PDF uploaded; a PDF always wins.
const getLegacyImagePages = (template) =>
  template?.templatePdfUrl
    ? []
    : (Array.isArray(template?.pages) ? template.pages : []).filter((p) => p && p.imageUrl);


// Where a "Property" field can read from.
// properties          -> the property record itself
// managementCompanies -> the management company on that property, joined by name
const SOURCE_COLLECTIONS = [
  { value: "properties", label: "Property record" },
  { value: "managementCompanies", label: "Management company" },
];

const EXCLUDED_MANAGEMENT_FIELDS = [
  "id",
  "lastEditedAt",
  "lastEditedBy",
];

const EXCLUDED_PROPERTY_FIELDS = [
  "id",
  "lastEditedAt",
  "lastEditedBy",
  "fileUpload",
];

const DATE_LIKE_FIELDS = [
  "dporExp",
  "sccExp",
  "insuranceExp",
  "dcDate",
];

const TEXTAREA_LIKE_FIELDS = [
  "notes",
];

function hexToRgbPdfLib(hex) {
  const clean = (hex || "#111111").replace("#", "");
  const normalized =
    clean.length === 3
      ? clean.split("").map((c) => c + c).join("")
      : clean;

  const num = parseInt(normalized, 16);

  return rgb(
    ((num >> 16) & 255) / 255,
    ((num >> 8) & 255) / 255,
    (num & 255) / 255
  );
}

async function getEmbeddedFont(pdfDoc, fontFamily, fontStyle = "regular") {
  const isBold = fontStyle === "bold";
  const isItalic = fontStyle === "italic";

  if (fontFamily === "TimesRoman") {
    if (isBold) return pdfDoc.embedFont(StandardFonts.TimesRomanBold);
    if (isItalic) return pdfDoc.embedFont(StandardFonts.TimesRomanItalic);
    return pdfDoc.embedFont(StandardFonts.TimesRoman);
  }

  if (fontFamily === "Courier") {
    if (isBold) return pdfDoc.embedFont(StandardFonts.CourierBold);
    if (isItalic) return pdfDoc.embedFont(StandardFonts.CourierOblique);
    return pdfDoc.embedFont(StandardFonts.Courier);
  }

  if (isBold) return pdfDoc.embedFont(StandardFonts.HelveticaBold);
  if (isItalic) return pdfDoc.embedFont(StandardFonts.HelveticaOblique);
  return pdfDoc.embedFont(StandardFonts.Helvetica);
}

// Draws the single divider page that sits between the report and the first
// attached proposal. One page total, listing every exhibit in the same order
// the proposals follow it, so the reader knows what is coming before they hit
// it. Sized to match the report's own pages rather than hardcoding Letter, so
// the document stays visually consistent up to the point the proposals begin.
async function drawExhibitDividerPage(pdfDoc, attachments = []) {
  if (!attachments.length) return;

  const reportPages = pdfDoc.getPages();
  const { width, height } = reportPages.length
    ? reportPages[0].getSize()
    : { width: 612, height: 792 };

  const page = pdfDoc.addPage([width, height]);

  const titleFont = await getEmbeddedFont(pdfDoc, "Helvetica", "bold");
  const bodyFont = await getEmbeddedFont(pdfDoc, "Helvetica", "regular");

  const margin = Math.min(72, width * 0.1);
  const contentWidth = width - margin * 2;
  const ink = hexToRgbPdfLib("#111111");

  const titleText = "EXHIBITS";
  const titleSize = 22;
  const entrySize = 13;
  const lineStep = entrySize * 1.45;
  const gapTitleToRule = 14;
  const gapRuleToEntries = lineStep * 1.6;
  const gapBetweenEntries = lineStep * 0.35;

  // --- Measure first, draw second --------------------------------------
  // Everything is laid out centered on the page, so the total height of the
  // block has to be known before the first baseline can be placed.
  const entries = attachments.map((attachment, index) => {
    // pdf-lib's standard fonts are WinAnsi-encoded and throw on anything
    // outside it. A scope pasted from Word with an odd glyph would otherwise
    // fail the whole report, so normalize the common offenders and drop the
    // rest rather than losing the document over one character.
    const safeScope = String(proposalLabel(attachment))
      .replace(/[\u2018\u2019\u201A\u2032]/g, "'")
      .replace(/[\u201C\u201D\u201E\u2033]/g, '"')
      .replace(/[\u2013\u2014]/g, "-")
      .replace(/\u2026/g, "...")
      .replace(/[^\x20-\x7E\xA0-\xFF]/g, "");

    // The en dash separator is WinAnsi-encodable, so it survives; any en
    // dashes inside the scope itself are flattened to hyphens above, which
    // keeps the separator unambiguous.
    const label = `Exhibit ${exhibitLetter(index)} \u2013 ${safeScope}`;

    // Long scopes wrap instead of running off the page, using the same
    // wrapper the field rendering already uses. Each wrapped line is centered
    // on its own, so there is no hanging indent here.
    return wrapLineForPdf(label, bodyFont, entrySize, contentWidth);
  });

  const entriesHeight =
    entries.reduce((sum, lines) => sum + lines.length * lineStep + gapBetweenEntries, 0) -
    gapBetweenEntries;

  const blockHeight =
    titleSize + gapTitleToRule + gapRuleToEntries + entriesHeight;

  // Centered vertically, but never pushed above the top margin if the list is
  // long enough to fill the page.
  const blockTop = Math.min(
    height - margin,
    (height + blockHeight) / 2
  );

  const centeredX = (text, font, size) =>
    (width - font.widthOfTextAtSize(text, size)) / 2;

  // --- Draw --------------------------------------------------------------
  let cursorY = blockTop - titleSize;

  page.drawText(titleText, {
    x: centeredX(titleText, titleFont, titleSize),
    y: cursorY,
    size: titleSize,
    font: titleFont,
    color: ink,
  });

  // Rule under the title, sized to the wider of the title and the longest
  // entry line so it frames the centered block instead of running the full
  // page width past short text.
  const widestLine = entries
    .flat()
    .reduce(
      (widest, line) =>
        Math.max(widest, bodyFont.widthOfTextAtSize(line, entrySize)),
      titleFont.widthOfTextAtSize(titleText, titleSize)
    );

  const ruleWidth = Math.min(contentWidth, widestLine);
  const ruleX = (width - ruleWidth) / 2;

  cursorY -= gapTitleToRule;
  page.drawLine({
    start: { x: ruleX, y: cursorY },
    end: { x: ruleX + ruleWidth, y: cursorY },
    thickness: 1,
    color: ink,
  });

  cursorY -= gapRuleToEntries;

  entries.forEach((lines) => {
    lines.forEach((line) => {
      if (cursorY < margin) return; // Out of room; one page only, by design.

      page.drawText(line, {
        x: centeredX(line, bodyFont, entrySize),
        y: cursorY,
        size: entrySize,
        font: bodyFont,
        color: ink,
      });

      cursorY -= lineStep;
    });

    cursorY -= gapBetweenEntries;
  });
}
function prettifyFieldLabel(fieldName) {
  return fieldName
    .replace(/\./g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function flattenObject(obj, prefix = "") {
  let result = {};

  Object.entries(obj || {}).forEach(([key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !(value instanceof Date) &&
      !("seconds" in value && "nanoseconds" in value)
    ) {
      result = { ...result, ...flattenObject(value, fullKey) };
    } else {
      result[fullKey] = value;
    }
  });

  return result;
}

function detectFieldType(fieldName, value) {
  if (TEXTAREA_LIKE_FIELDS.includes(fieldName)) return "textarea";
  if (DATE_LIKE_FIELDS.includes(fieldName)) return "date";

  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    return "date";
  }

  return "text";
}

const getSourceCollection = (field = {}) =>
  field.sourceCollection === "managementCompanies" ? "managementCompanies" : "properties";

// The option a dbField points at. Matched on collection + field name, because
// "management" exists in both collections and they mean different things.
const findSourceOption = (options = [], field = {}) => {
  const collection = getSourceCollection(field);
  return (
    options.find(
      (opt) => opt.collection === collection && opt.value === field.sourceField
    ) || null
  );
};

// Same join Approved Invoices uses: a property stores the management company's
// NAME, so the company record is looked up by that name.
const findManagementRecord = (property, managementRecords = []) => {
  const name = String(property?.management || property?.managementCompany || "").trim();
  if (!name) return null;
  return (
    managementRecords.find((record) =>
      [record.management, record.managementCompany, record.companyName, record.name]
        .filter(Boolean)
        .some((candidate) => String(candidate).trim() === name)
    ) || null
  );
};

const getNestedValue = (obj, path) => {
  if (!obj || !path) return "";
  return path.split(".").reduce((acc, key) => acc?.[key], obj) ?? "";
};

const DEFAULT_REPORT_VALUES = {
  siteName: "",
  siteAddressBlock: "",
  companyName: "Priority Pest Defense",
  companyAddress: "",
  propertyAddress: "",
  internalNotes: "",
  reportStatus: "Draft",
};

// ---------------------------------------------------------------------------
// Proposal attachments
//
// ProposalsPPD writes these four fields onto the proposal doc every time
// Print/Download runs:
//   latestProposalPdfUrl / latestProposalPdfName /
//   latestProposalPdfStoragePath / latestProposalPdfGeneratedAt
// so the picker never has to list Storage. A proposal without
// latestProposalPdfStoragePath has simply never been printed yet; it is still
// listed, but disabled, so a proposal you expected to see is never silently
// missing from the list.
//
// The join is exact, not fuzzy: Reports.handleSiteSelection and
// ProposalsPPD both copy properties.propertyName into
// siteName, so an equality query is enough.
// ---------------------------------------------------------------------------

// Warn once the merged packet gets big enough that mail servers start
// rejecting it. 15MB is under the common 20-25MB limits with headroom for
// base64 encoding overhead.
const ATTACHMENT_SIZE_WARN_BYTES = 15 * 1024 * 1024;

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// latestProposalPdfGeneratedAt may be a Firestore Timestamp, a Date, or the
// ISO / display string older proposals were written with. Normalize all three
// so sorting "most recent first" actually works.
const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

// Proposals have no number field of their own, so scope is the identity the
// office actually recognizes, with the submitted date as the tiebreaker.
const proposalLabel = (proposal) =>
  String(proposal?.scope || "").trim() || "No scope";

// 0 -> A, 25 -> Z, 26 -> AA. Spreadsheet-column style, so the divider keeps
// working past 26 attachments instead of running off the end of the alphabet.
const exhibitLetter = (index) => {
  let n = Number(index);
  if (!Number.isFinite(n) || n < 0) return "";

  let out = "";
  n += 1;
  while (n > 0) {
    const remainder = (n - 1) % 26;
    out = String.fromCharCode(65 + remainder) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

// A date input gives back "YYYY-MM-DD". new Date("2026-10-02") is specified to
// parse that as midnight UTC, but getDate() then reads it back in local time —
// so anywhere west of UTC it comes out as the day before. Pick 10/02/2026 in
// Washington and you get 10/01/2026 on the PDF.
//
// Fix it at the source: build date-only strings as a local date so the numbers
// you typed are the numbers that print. Timestamps that carry a real time and
// zone (ISO strings with a T, Date objects, Firestore Timestamps) are already
// unambiguous, so they keep going through the normal parse.
function formatDateMMDDYYYY(value) {
  if (!value) return "";

  let d;
  const dateOnly =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());

  if (dateOnly) {
    const [yyyyStr, mmStr, ddStr] = value.trim().split("-");
    d = new Date(Number(yyyyStr), Number(mmStr) - 1, Number(ddStr));
  } else {
    d = new Date(value);
  }

  if (Number.isNaN(d.getTime())) return value;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function getDisplayValue(field, value, allPropertyFieldOptions = []) {
  if (field.type === "date" || field.type === "datePicker") {
    return formatDateMMDDYYYY(value);
  }

  if (field.type === "dbField") {
    const selectedOption = findSourceOption(allPropertyFieldOptions, field);

    if (selectedOption?.type === "date") {
      return formatDateMMDDYYYY(value);
    }
  }

  return value || "";
}

function SignaturePadField({ label, value, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!value) return;

    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = value;
  }, [value]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    return {
      x: ((clientX - rect.left) * canvas.width) / rect.width,
      y: ((clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPoint(e);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    onChange(canvas.toDataURL("image/png"));
  };

  const stopDrawing = () => setDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div className="sig-pad-wrap">
      <label>{label}</label>
      <canvas
        ref={canvasRef}
        width={320}
        height={120}
        className="sig-pad"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <button type="button" className="red-button" onClick={clearSignature}>
        Clear Signature
      </button>
    </div>
  );
}

function getEmptyField(type) {
  return {
    id: `field_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    label:
      type === "site"
        ? "Property Name"
        : type === "datePicker"
        ? "Date"
        : type === "dbField"
        ? "Database Field"
        : "New Field",
    type,
    page: 0,
    x: 40,
    y: 40,
    width:
      type === "checkbox"
        ? 20
        : type === "site"
        ? 260
        : type === "datePicker"
        ? 140
        : 180,
    height:
      type === "textarea"
        ? 48
        : type === "signature"
        ? 40
        : type === "site"
        ? 42
        : 20,
        fontSize: 14,
        fontFamily: "Helvetica",
fontStyle: "regular",
textAlign: "left",
fontColor: "#111111",
        letterSpacing: 0,
        lineHeight: 1.15,

    sourceCollection: "properties",
    sourceField: "",
    lookupBy: "propertyName",
    autoPopulate: true,
    // Position in the Fill Out the Report sidebar. Independent of where the
    // field sits on the PDF, so the list can be reordered freely.
    formOrder: 0,
  };
}

function FieldOverlay({ field, value, selected, onMouseDown, editMode, allPropertyFieldOptions }) {
  const style = {
    position: "absolute",
    zIndex: 50,
    pointerEvents: "auto",
    boxSizing: "border-box",
    overflow: "hidden",
    whiteSpace: "pre-wrap",
    left: field.x,
    top: field.y,
    width: field.width,
    height: field.height,
    fontSize: field.fontSize || 14,
    color: field.fontColor || "#111111",
    letterSpacing: `${field.letterSpacing ?? 0}px`,
    lineHeight: field.lineHeight ?? 1.15,
    fontFamily:
      field.fontFamily === "TimesRoman"
        ? '"Times New Roman", serif'
        : field.fontFamily === "Courier"
        ? '"Courier New", monospace'
        : "Arial, Helvetica, sans-serif",
        fontWeight: field.fontStyle === "bold" ? "700" : "400",
fontStyle: field.fontStyle === "italic" ? "italic" : "normal",
textAlign: field.textAlign || "left",
  };

  if (editMode) {
    return (
      <div
        className={`overlay-field overlay-field-edit ${selected ? "selected" : ""}`}
        style={{
          ...style,
          background: selected ? "rgba(37, 99, 235, 0.22)" : "rgba(37, 99, 235, 0.12)",
          border: selected ? "2px solid #1d4ed8" : "1px dashed #2563eb",
        }}
        onMouseDown={(e) => onMouseDown(e, field)}
        title={field.label}
      >
        <span className="overlay-field-badge">{field.label}</span>
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div className="overlay-field overlay-checkbox" style={style}>
        {value ? "X" : ""}
      </div>
    );
  }

  if (field.type === "signature") {
    return value ? (
      <img
        src={value}
        alt={field.label}
        className="overlay-field overlay-signature"
        style={style}
      />
    ) : null;
  }

  const displayValue = getDisplayValue(field, value, allPropertyFieldOptions);

  const selectedOption = findSourceOption(allPropertyFieldOptions, field);

  const isTextareaLike =
    field.type === "textarea" ||
    field.type === "site" ||
    (field.type === "dbField" && selectedOption?.type === "textarea");

  return (
    <div
      className={`overlay-field ${isTextareaLike ? "overlay-textarea" : "overlay-text"}`}
      style={style}
    >
      {displayValue}
    </div>
  );
}

function formatTimestamp(ts) {
  if (!ts) return "";

  try {
    const date =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : new Date(ts);

    return date.toLocaleString();
  } catch {
    return "";
  }
}

export default function Reports() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(DEFAULT_TEMPLATE);
  const [editMode, setEditMode] = useState(true);

  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportValues, setReportValues] = useState(DEFAULT_REPORT_VALUES);
  const [reportSearch, setReportSearch] = useState("");

  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState("");

  const [newTemplateName, setNewTemplateName] = useState("");
  const [queuedTemplatePdf, setQueuedTemplatePdf] = useState(null);
  const [pdfPreviewPageCount, setPdfPreviewPageCount] = useState(0);
  const [, setPageViewSizes] = useState({});  
  const [currentVisiblePage, setCurrentVisiblePage] = useState(0);
const pdfScrollRef = useRef(null);
  const [propertyRecords, setPropertyRecords] = useState([]);
  const [managementRecords, setManagementRecords] = useState([]);
  const [pdfZoom, setPdfZoom] = useState(100);
  const [templateDirty, setTemplateDirty] = useState(false);

  const [reportDirty, setReportDirty] = useState(false);

  const [activeStep, setActiveStep] = useState(1);

  // --- Proposal attachments -------------------------------------------------
  // attachedProposals is the packet that gets appended to the report PDF, in
  // the order shown. It is persisted on the report doc so regenerating a
  // report rebuilds the same packet instead of silently dropping pages.
  const [attachedProposals, setAttachedProposals] = useState([]);
  const [proposalPickerOpen, setProposalPickerOpen] = useState(false);
  const [proposalOptions, setProposalOptions] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [proposalPickerIds, setProposalPickerIds] = useState([]);

  const [pdfInputKey, setPdfInputKey] = useState(0);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const pageRefs = useRef({});
  const lastLoadedTemplateIdRef = useRef("");
  const stepCardRef = useRef(null);
  const wizardRef = useRef(null);
  const didPickInitialStepRef = useRef(false);

  const zoomFactor = pdfZoom / 100;

  const tabBtn = (active) => ({
    padding: "10px 14px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    borderBottom: active ? "1px solid white" : "1px solid #1d4ed8",
    background: active ? "#1d4ed8" : "#93c5fd",
    color: active ? "white" : "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
  });

  const loadAllData = async () => {
    const [templatesSnap, reportsSnap, propertiesSnap, managementSnap] = await Promise.all([
      getDocs(collection(db, "reportTemplates")),
      getDocs(collection(db, "reports")),
      getDocs(collection(db, "properties")),
      getDocs(collection(db, "managementCompanies")),
    ]);
  
    const storage = getStorage();

    const templateDocs = await Promise.all(
      templatesSnap.docs.map(async (d) => {
        const data = { id: d.id, ...d.data() };
    
        if (data.templatePdfStoragePath) {
          try {
            data.templatePdfUrl = await getDownloadURL(
              ref(storage, data.templatePdfStoragePath)
            );
          } catch (err) {
            console.error("Could not refresh template PDF URL:", data.name, err);
          }
        }

        // Legacy image-page templates: re-sign any uploaded page images.
        if (!data.templatePdfUrl && Array.isArray(data.pages)) {
          data.pages = await Promise.all(
            data.pages.map(async (page) => {
              if (!page?.storagePath) return page;
              try {
                const imageUrl = await getDownloadURL(ref(storage, page.storagePath));
                return { ...page, imageUrl };
              } catch (err) {
                console.error("Could not refresh template page URL:", data.name, err);
                return page;
              }
            })
          );
        }

        return data;
      })
    );

const reportDocs = reportsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
const propertyDocs = propertiesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  
    setPropertyRecords(
      propertyDocs.sort((a, b) =>
        (a.propertyName || "").localeCompare(b.propertyName || "")
      )
    );

    setManagementRecords(
      managementSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
    );
  
    if (templateDocs.length === 0) {
      const docRef = await addDoc(collection(db, "reportTemplates"), {
        ...DEFAULT_TEMPLATE,
        createdAt: serverTimestamp(),
      });
      const createdTemplate = { id: docRef.id, ...DEFAULT_TEMPLATE };
      setTemplates([createdTemplate]);
      setSelectedTemplate(createdTemplate);
      setSelectedTemplateId(docRef.id);
    } else {
      setTemplates(templateDocs);
      const found = templateDocs.find((t) => t.id === selectedTemplateId) || templateDocs[0];
      setSelectedTemplate(found);
      setSelectedTemplateId(found.id);

      // First load only: a template that is already built opens on Step 4
      // (fill out a report). Anything unfinished opens on Step 1.
      if (!didPickInitialStepRef.current) {
        didPickInitialStepRef.current = true;
        const ready =
          (!!found.templatePdfUrl || getLegacyImagePages(found).length > 0) &&
          (found.fields || []).length > 0;
        setActiveStep(ready ? 4 : 1);
        setEditMode(!ready);
      }
    }
  
    setReports(reportDocs);
  };

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps the editor in sync with the dropdown.
  // Before: this ran on every `templates` change (rename, duplicate, opening a
  // report) and replaced the template being edited with the last-saved copy,
  // silently wiping field changes that had not been saved yet.
  // Now: a different template always loads fresh from the saved list; the same
  // template is only refreshed when there are no unsaved changes.
  useEffect(() => {
    if (!templates.length) return;
    const found = templates.find((t) => t.id === selectedTemplateId) || templates[0];
    if (!found) return;

    const sameTemplate = lastLoadedTemplateIdRef.current === found.id;
    if (sameTemplate && templateDirty) return;

    setSelectedTemplate(found);

    if (!sameTemplate) {
      lastLoadedTemplateIdRef.current = found.id;
      setQueuedTemplatePdf(null);
      setPdfInputKey((k) => k + 1);
      setSelectedFieldId(null);
      setPdfPreviewPageCount(0);
      setPageViewSizes({});
      setCurrentVisiblePage(0);
      pageRefs.current = {};
      setTemplateDirty(false);
    }
  }, [selectedTemplateId, templates, templateDirty]);

  const selectedField = useMemo(
    () => selectedTemplate?.fields?.find((f) => f.id === selectedFieldId) || null,
    [selectedTemplate, selectedFieldId]
  );

  // Everything a Property field can be pointed at, from both collections.
  // Nothing is hardcoded: whatever Properties & Clients and Management hold
  // shows up here on its own.
  const allPropertyFieldOptions = useMemo(() => {
    const discover = (records, collectionName, excluded) => {
      const found = new Map();

      records.forEach((record) => {
        const flat = flattenObject(record);

        Object.entries(flat).forEach(([key, value]) => {
          if (excluded.includes(key)) return;
          if (found.has(key)) return;

          found.set(key, {
            label: prettifyFieldLabel(key),
            value: key,
            collection: collectionName,
            type: detectFieldType(key, value),
          });
        });
      });

      return Array.from(found.values()).sort((a, b) => a.label.localeCompare(b.label));
    };

    return [
      ...discover(propertyRecords, "properties", EXCLUDED_PROPERTY_FIELDS),
      ...discover(managementRecords, "managementCompanies", EXCLUDED_MANAGEMENT_FIELDS),
    ];
  }, [propertyRecords, managementRecords]);

  // One place that answers "what should this field show for this property?",
  // used by both the auto-fill effect and the Property picker.
  const readSourceValue = useCallback(
    (field, property) => {
      if (!property) return "";

      if (getSourceCollection(field) === "managementCompanies") {
        const company = findManagementRecord(property, managementRecords);
        if (!company) return "";
        return getNestedValue(company, field.sourceField) ?? "";
      }

      return getNestedValue(property, field.sourceField) ?? "";
    },
    [managementRecords]
  );

  // Sidebar order for Fill Out the Report. Fields without a saved order keep
  // the order they were added in, so old templates are unaffected.
  const orderedFormFields = useMemo(() => {
    const fields = selectedTemplate?.fields || [];

    return fields
      .map((field, index) => ({
        field,
        index,
        order: Number.isFinite(Number(field.formOrder)) ? Number(field.formOrder) : index,
      }))
      .sort((a, b) => a.order - b.order || a.index - b.index)
      .map((item) => item.field);
  }, [selectedTemplate]);

  // Moves a field up or down in the sidebar only. Nothing on the PDF moves:
  // page, x and y are untouched.
  const moveFormField = (fieldId, direction) => {
    const ordered = orderedFormFields;
    const from = ordered.findIndex((f) => f.id === fieldId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ordered.length) return;

    const next = [...ordered];
    [next[from], next[to]] = [next[to], next[from]];

    const orderById = new Map(next.map((f, index) => [f.id, index]));

    setSelectedTemplate((prev) => ({
      ...prev,
      fields: (prev.fields || []).map((f) =>
        orderById.has(f.id) ? { ...f, formOrder: orderById.get(f.id) } : f
      ),
    }));
    setTemplateDirty(true);
  };

  const filteredReports = useMemo(() => {
    const q = reportSearch.trim().toLowerCase();
    if (!q) return reports;

    return reports.filter((report) => {
      const siteName = (report.siteName || report.values?.siteName || "").toLowerCase();
      const propertyAddress = (report.propertyAddress || report.values?.propertyAddress || "").toLowerCase();
      const reportNumber = String(report.reportNumber || "").toLowerCase();
      const templateName = (report.templateName || "").toLowerCase();

      return (
        siteName.includes(q) ||
        propertyAddress.includes(q) ||
        reportNumber.includes(q) ||
        templateName.includes(q)
      );
    });
  }, [reports, reportSearch]);

  const handleAddField = (type) => {
    const existing = selectedTemplate.fields || [];
    const highestOrder = existing.reduce(
      (max, field, index) =>
        Math.max(max, Number.isFinite(Number(field.formOrder)) ? Number(field.formOrder) : index),
      -1
    );

    const newField = {
      ...getEmptyField(type),
      page: currentVisiblePage,
      x: 60,
      y: 60,
      formOrder: highestOrder + 1,
    };
  
    const next = {
      ...selectedTemplate,
      fields: [...(selectedTemplate.fields || []), newField],
    };
  
    setSelectedTemplate(next);
    setSelectedFieldId(newField.id);
    setTemplateDirty(true);
  };

  // The picker's value is "collection:fieldName" so the same name in two
  // collections stays distinct.
  const handleDbFieldSourceChange = (comboValue) => {
    if (!selectedFieldId) return;

    const [sourceCollection, ...rest] = String(comboValue || "").split(":");
    const sourceFieldValue = rest.join(":");

    const selectedOption = allPropertyFieldOptions.find(
      (opt) => opt.collection === sourceCollection && opt.value === sourceFieldValue
    );
  
    setSelectedTemplate((prev) => {
      const nextFields = (prev.fields || []).map((field) => {
        if (field.id !== selectedFieldId) return field;
  
        const nextField = {
          ...field,
          sourceCollection: sourceCollection || "properties",
          sourceField: sourceFieldValue,
        };
  
        if (selectedOption) {
          nextField.label = selectedOption.label;
          nextField.height = selectedOption.type === "textarea" ? 48 : 20;
  
          if (selectedOption.type === "textarea") {
            nextField.width = 260;
          }
        }
  
        return nextField;
      });
  
      return { ...prev, fields: nextFields };
    });
    setTemplateDirty(true);
  };

  const handlePageRenderSuccess = (pageIndex, pageEl) => {
    if (!pageEl) return;
  
    const rect = pageEl.getBoundingClientRect();
    const nextWidth = Math.round(rect.width);
    const nextHeight = Math.round(rect.height);
  
    setPageViewSizes((prev) => {
      const current = prev[pageIndex];
  
      if (
        current &&
        current.width === nextWidth &&
        current.height === nextHeight
      ) {
        return prev;
      }
  
      return {
        ...prev,
        [pageIndex]: {
          width: nextWidth,
          height: nextHeight,
        },
      };
    });
  };

  const handlePdfScroll = () => {
    const scrollBox = pdfScrollRef.current;
    if (!scrollBox) return;
  
    let closestPage = 0;
    let smallestDistance = Infinity;
  
    Object.entries(pageRefs.current).forEach(([pageIndex, pageEl]) => {
      if (!pageEl) return;
  
      const pageRect = pageEl.getBoundingClientRect();
      const boxRect = scrollBox.getBoundingClientRect();
  
      const distance = Math.abs(pageRect.top - boxRect.top);
  
      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestPage = Number(pageIndex);
      }
    });
  
    setCurrentVisiblePage(closestPage);
  };

  const legacyImagePages = useMemo(
    () => getLegacyImagePages(selectedTemplate),
    [selectedTemplate]
  );

  const totalPages = selectedTemplate.templatePdfUrl
    ? selectedTemplate.pageCount || pdfPreviewPageCount || 0
    : legacyImagePages.length;

const goToPage = (pageNumber) => {
  const requestedPage = Math.min(Math.max(Number(pageNumber), 1), totalPages);
  const pageIndex = requestedPage - 1;
  const pageEl = pageRefs.current[pageIndex];

  if (!pageEl) return;

  pageEl.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });

  setCurrentVisiblePage(pageIndex);
};

  const handleFieldPropChange = (key, value) => {
    if (!selectedFieldId) return;
  
    setSelectedTemplate((prev) => {
      const nextFields = (prev.fields || []).map((field) =>
        field.id === selectedFieldId
          ? {
              ...field,
              [key]:
              ["x", "y", "width", "height", "fontSize", "page", "letterSpacing", "lineHeight"].includes(key)
                  ? Number(value)
                  : value,
            }
          : field
      );
  
      return { ...prev, fields: nextFields };
    });
    setTemplateDirty(true);
  };

  const handleDeleteField = () => {
    if (!selectedField) return;
    const nextFields = selectedTemplate.fields.filter((f) => f.id !== selectedField.id);
    setSelectedTemplate({ ...selectedTemplate, fields: nextFields });
    setSelectedFieldId(null);
    setTemplateDirty(true);
  };

  const handleMouseDownField = (e, field) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();

    setSelectedFieldId(field.id);

    const pageEl = pageRefs.current[field.page];
    if (!pageEl) return;

    setDragState({
      fieldId: field.id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: field.x,
      startY: field.y,
      scale: zoomFactor || 1,
    });
  };

  useEffect(() => {
    const beforeUnload = (e) => {
      if (!templateDirty) return;
  
      e.preventDefault();
      e.returnValue = "";
    };
  
    window.addEventListener("beforeunload", beforeUnload);
  
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [templateDirty]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragState || !selectedTemplate) return;

      // Mouse movement is in screen pixels; fields are stored at 100% zoom.
      const dx = (e.clientX - dragState.startMouseX) / (dragState.scale || 1);
      const dy = (e.clientY - dragState.startMouseY) / (dragState.scale || 1);

      const nextFields = selectedTemplate.fields.map((field) =>
        field.id === dragState.fieldId
          ? {
              ...field,
              x: Math.max(0, Math.round(dragState.startX + dx)),
              y: Math.max(0, Math.round(dragState.startY + dy)),
            }
          : field
      );

      setSelectedTemplate((prev) => ({ ...prev, fields: nextFields }));
      setTemplateDirty(true);
    };

    const onUp = () => setDragState(null);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragState, selectedTemplate]);

  const goToStep = (step, anchor = "step") => {
    setActiveStep(step);
    // Steps 1-3 build the template (show field boxes); 4-5 show filled values.
    setEditMode(step <= 3);
    requestAnimationFrame(() => {
      const target = anchor === "wizard" ? wizardRef.current : stepCardRef.current;
      target?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  // Called before anything that loads a different template.
  // Returns true when it is safe to continue.
  const confirmLeaveDirtyTemplate = async () => {
    if (!templateDirty) return true;

    const shouldSave = window.confirm(
      `You have unsaved field changes on "${selectedTemplate?.name || "this template"}".\n\n` +
        "OK = Save them first\nCancel = More options"
    );

    if (shouldSave) {
      return handleTemplateSave({ silent: true });
    }

    const discard = window.confirm(
      "Continue without saving? Your unsaved field changes will be lost.\n\n" +
        "OK = Discard changes\nCancel = Stay on this template"
    );

    if (discard) {
      setTemplateDirty(false);
      return true;
    }

    return false;
  };

  const handleTemplateDropdownChange = async (nextId) => {
    if (!nextId || nextId === selectedTemplateId) return;

    const ok = await confirmLeaveDirtyTemplate();
    if (!ok) return;

    // A saved report belongs to one template. Switching templates starts a new
    // blank report but keeps the chosen property so Property fields refill.
    if (selectedReport?.id && selectedReport.templateId !== nextId) {
      setSelectedReport(null);
      setReportValues((prev) => ({
        ...DEFAULT_REPORT_VALUES,
        siteName: prev.siteName || "",
        propertyAddress: prev.propertyAddress || "",
      }));
      setPdfDownloadUrl("");
      setReportDirty(false);
    }

    setSelectedTemplateId(nextId);
  };

  const handleCreateTemplate = async () => {
    const trimmed = newTemplateName.trim();
    if (!trimmed) {
      alert("Enter a template name first.");
      return;
    }

    if (!(await confirmLeaveDirtyTemplate())) return;

    try {
      const payload = {
        key: `template_${Date.now()}`,
        name: trimmed,
        templatePdfUrl: "",
        templatePdfStoragePath: "",
        templatePdfFileName: "",
        pageCount: 0,
        fields: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, "reportTemplates"), payload);
      const created = { id: docRef.id, ...payload };

      setTemplates((prev) => [...prev, created]);
      setSelectedTemplate(created);
      setSelectedTemplateId(docRef.id);
      setNewTemplateName("");
      setQueuedTemplatePdf(null);
      setSelectedFieldId(null);
      setTemplateDirty(false);

      alert("Template created. Next: upload its PDF.");
      goToStep(2);
    } catch (err) {
      console.error(err);
      alert("Could not create template.");
    }
  };

  const handleDuplicateTemplate = async () => {
    if (!selectedTemplate?.id) {
      alert("Select a template first.");
      return;
    }
  
    if (templateDirty) {
      const saveFirst = window.confirm(
        "Save your field changes to this template before duplicating it?\n\n" +
          "OK = Save, then duplicate\nCancel = Duplicate without saving (the copy still gets your changes)"
      );
      if (saveFirst && !(await handleTemplateSave({ silent: true }))) return;
    }

    try {
      const duplicated = {
        ...sanitizeTemplateForSave(selectedTemplate),
        key: `template_${Date.now()}`,
        name: `${selectedTemplate.name} Copy`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
  
      const docRef = await addDoc(
        collection(db, "reportTemplates"),
        duplicated
      );
  
      const created = {
        id: docRef.id,
        ...duplicated,
      };
  
      setTemplateDirty(false);
      setTemplates((prev) => [...prev, created]);
      setSelectedTemplate(created);
      setSelectedTemplateId(docRef.id);
  
      alert("Template duplicated.");
    } catch (err) {
      console.error(err);
      alert("Could not duplicate template.");
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate?.id) return;

    const confirmDelete = window.confirm(
      `Delete template "${selectedTemplate.name}"? This will remove the template record and its uploaded PDF template.`
    );
    if (!confirmDelete) return;

    try {
      const storage = getStorage();

      if (selectedTemplate.templatePdfStoragePath) {
        try {
          await deleteObject(ref(storage, selectedTemplate.templatePdfStoragePath));
        } catch (err) {
          console.warn("Could not delete template PDF:", err);
        }
      }

      await deleteDoc(doc(db, "reportTemplates", selectedTemplate.id));

      const remaining = templates.filter((t) => t.id !== selectedTemplate.id);
      setTemplates(remaining);

      if (remaining.length) {
        setSelectedTemplate(remaining[0]);
        setSelectedTemplateId(remaining[0].id);
      } else {
        setSelectedTemplate(DEFAULT_TEMPLATE);
        setSelectedTemplateId("");
      }

      setQueuedTemplatePdf(null);
      setSelectedFieldId(null);
      setTemplateDirty(false);

      alert("Template deleted.");
    } catch (err) {
      console.error(err);
      alert("Could not delete template.");
    }
  };

  const handleRenameTemplate = async () => {
    if (!selectedTemplate?.id) {
      alert("Select a template first.");
      return;
    }
  
    const newName = window.prompt(
      "Enter new template name:",
      selectedTemplate.name || ""
    );
  
    if (!newName?.trim()) return;
  
    try {
      const trimmedName = newName.trim();
  
      await updateDoc(doc(db, "reportTemplates", selectedTemplate.id), {
        name: trimmedName,
        updatedAt: serverTimestamp(),
      });
  
      setSelectedTemplate((prev) => ({
        ...prev,
        name: trimmedName,
      }));
  
      setTemplates((prev) =>
        prev.map((template) =>
          template.id === selectedTemplate.id
            ? { ...template, name: trimmedName }
            : template
        )
      );
  
      alert("Template renamed.");
    } catch (err) {
      console.error(err);
      alert("Could not rename template.");
    }
  };

  const handleTemplateSave = async ({ silent = false } = {}) => {
    if (savingTemplate) return false;
    setSavingTemplate(true);

    try {
      const payload = sanitizeTemplateForSave(selectedTemplate);
      let savedId = selectedTemplate?.id || "";

      if (!savedId) {
        const docRef = await addDoc(collection(db, "reportTemplates"), {
          ...payload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        savedId = docRef.id;
      } else {
        await updateDoc(doc(db, "reportTemplates", savedId), {
          ...payload,
          updatedAt: serverTimestamp(),
        });
      }

      // Read it back so the dropdown list holds exactly what Firestore has.
      // Picking this template again later loads this copy, fields included.
      const savedSnap = await getDoc(doc(db, "reportTemplates", savedId));
      const savedData = savedSnap.exists() ? savedSnap.data() : payload;
      const saved = {
        id: savedId,
        ...savedData,
        // keep the freshly-signed URL we are already displaying
        templatePdfUrl:
          selectedTemplate?.templatePdfUrl || savedData.templatePdfUrl || "",
      };

      const savedFieldCount = (saved.fields || []).length;
      const localFieldCount = (selectedTemplate?.fields || []).length;
      if (savedFieldCount !== localFieldCount) {
        throw new Error(
          `Saved ${savedFieldCount} fields but the editor has ${localFieldCount}.`
        );
      }

      lastLoadedTemplateIdRef.current = savedId;
      setTemplateDirty(false);
      setTemplates((prev) =>
        prev.some((t) => t.id === savedId)
          ? prev.map((t) => (t.id === savedId ? saved : t))
          : [...prev, saved]
      );
      setSelectedTemplate(saved);
      setSelectedTemplateId(savedId);

      if (!silent) {
        alert(`Template saved with ${savedFieldCount} field${savedFieldCount === 1 ? "" : "s"}.`);
      }
      return true;
    } catch (err) {
      console.error("Template save failed:", err);
      alert(`Could not save template. ${err?.message || ""}`.trim());
      return false;
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleUploadTemplatePdf = async () => {
    if (!selectedTemplate?.id) {
      alert("Select or create a template first.");
      return;
    }
  
    if (!queuedTemplatePdf) {
      alert("Choose a PDF template first.");
      return;
    }

    setUploadingPdf(true);
  
    try {
      // Check the page count before uploading anything.
      const pdfBytes = await queuedTemplatePdf.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pageCount = pdfDoc.getPageCount();

      if (pageCount > MAX_TEMPLATE_PAGES) {
        alert(
          `This PDF has ${pageCount} pages. Templates can have up to ${MAX_TEMPLATE_PAGES}.`
        );
        return;
      }

      const storage = getStorage();
      const previousPath = selectedTemplate.templatePdfStoragePath || "";
      const storagePath = `reportTemplates/${selectedTemplate.id}/template/${queuedTemplatePdf.name}`;
      const storageRef = ref(storage, storagePath);
  
      await uploadBytes(storageRef, queuedTemplatePdf, {
        contentType: "application/pdf",
      });
  
      const templatePdfUrl = await getDownloadURL(storageRef);
  
      const nextTemplate = {
        ...selectedTemplate,
        templatePdfUrl,
        templatePdfStoragePath: storagePath,
        templatePdfFileName: queuedTemplatePdf.name,
        pageCount,
      };

      // Before: only the PDF info was written, then every template was reloaded
      // from Firestore — any fields placed but not yet saved were thrown away.
      // Now the whole template (fields included) is saved with the new PDF.
      await updateDoc(doc(db, "reportTemplates", selectedTemplate.id), {
        ...sanitizeTemplateForSave(nextTemplate),
        updatedAt: serverTimestamp(),
      });

      if (previousPath && previousPath !== storagePath) {
        try {
          await deleteObject(ref(storage, previousPath));
        } catch (cleanupErr) {
          console.warn("Old template PDF was not removed:", cleanupErr);
        }
      }

      lastLoadedTemplateIdRef.current = nextTemplate.id;
      setTemplateDirty(false);
      setTemplates((prev) =>
        prev.map((t) => (t.id === nextTemplate.id ? nextTemplate : t))
      );
      setSelectedTemplate(nextTemplate);
      setQueuedTemplatePdf(null);
      setPdfInputKey((k) => k + 1);
      setPdfPreviewPageCount(pageCount);
      setPageViewSizes({});
      pageRefs.current = {};

      const outsidePages = (nextTemplate.fields || []).filter(
        (f) => Number(f.page) >= pageCount
      ).length;

      alert(
        outsidePages
          ? `Template PDF uploaded. ${outsidePages} field(s) sit on pages this PDF doesn't have — move them in Step 3.`
          : "Template PDF uploaded. Next: place your fields."
      );
      goToStep(3);
    } catch (err) {
      console.error(err);
      alert(`Could not upload template PDF. ${err?.message || ""}`.trim());
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleQueueTemplatePdf = (fileList) => {
    const file = fileList?.[0] || null;
  
    if (!file) {
      setQueuedTemplatePdf(null);
      return;
    }
  
    if (file.type !== "application/pdf") {
      alert("Please choose a PDF template.");
      return;
    }
  
    setQueuedTemplatePdf(file);
  };

  useEffect(() => {
    if (!selectedTemplate?.fields?.length) return;
    if (!reportValues.siteName) return;
  
    const matchedProperty = propertyRecords.find(
      (p) => (p.propertyName || "").trim() === (reportValues.siteName || "").trim()
    );
  
    if (!matchedProperty) return;
  
    setReportValues((prev) => {
      const next = { ...prev };
      let changed = false;
  
      selectedTemplate.fields.forEach((field) => {
        if (field.type === "site") {
          const siteValue = matchedProperty.propertyName || "";
          if (next[field.id] !== siteValue) {
            next[field.id] = siteValue;
            changed = true;
          }
        }
      
        if (field.type === "dbField" && field.autoPopulate) {
          const value = readSourceValue(field, matchedProperty) ?? "";
      
          if (next[field.id] !== value) {
            next[field.id] = value;
            changed = true;
          }
        }
      });
  
      return changed ? next : prev;
    });
  }, [reportValues.siteName, propertyRecords, selectedTemplate, readSourceValue]);

  const handleReportValueChange = (fieldId, value) => {
    setReportValues((prev) => ({ ...prev, [fieldId]: value }));
    setReportDirty(true);
  };

  const handleSiteSelection = (siteName) => {
    const matchedProperty = propertyRecords.find(
      (p) => (p.propertyName || "").trim() === (siteName || "").trim()
    );

    // Attached proposals belong to the site they were picked for. Changing the
    // site silently keeping another site's proposals attached would be a very
    // bad surprise on a document that gets emailed out, so drop them.
    const previousSite = (reportValues.siteName || "").trim();
    const nextSite = (siteName || "").trim();

    if (attachedProposals.length && previousSite && previousSite !== nextSite) {
      setAttachedProposals([]);
      alert(
        `Site changed to "${nextSite || "none"}". The attached proposals from "${previousSite}" were removed. Use Add Proposal to attach proposals for the new site.`
      );
    }
  
    if (!matchedProperty) {
      setReportValues((prev) => {
        const cleared = {
          ...prev,
          siteName: "",
          propertyAddress: "",
        };
  
        (selectedTemplate?.fields || []).forEach((field) => {
          if (field.type === "site") {
            cleared[field.id] = "";
          }
  
          if (field.type === "dbField" && field.autoPopulate) {
            cleared[field.id] = "";
          }
        });
  
        return cleared;
      });
      return;
    }
  
    const formattedAddress = [
      matchedProperty.address || "",
      [matchedProperty.city, matchedProperty.state, matchedProperty.zip]
        .filter(Boolean)
        .join(", "),
    ]
      .filter(Boolean)
      .join("\n");
  
    setReportValues((prev) => {
      const next = {
        ...prev,
        siteName: matchedProperty.propertyName || "",
        propertyAddress: formattedAddress,
      };
  
      (selectedTemplate?.fields || []).forEach((field) => {
        if (field.type === "site") {
          next[field.id] = matchedProperty.propertyName || "";
        }
  
        if (field.type === "dbField" && field.autoPopulate) {
          next[field.id] = readSourceValue(field, matchedProperty) ?? "";
        }
      });
  
      return next;
    });
    setReportDirty(true);
  };

  // --- Proposal attachments -------------------------------------------------

  // Equality-only query on purpose. Adding orderBy on a different field would
  // require a composite index deployment; there are few enough proposals per
  // site that sorting in memory is cheaper than the index.
  const handleOpenProposalPicker = async () => {
    const site = (reportValues.siteName || "").trim();

    if (!site) {
      alert("Pick a site first. Proposals are matched to the report by site name.");
      return;
    }

    setProposalPickerOpen(true);
    setLoadingProposals(true);
    setProposalPickerIds(attachedProposals.map((p) => p.proposalId));

    try {
      const snap = await getDocs(
        query(collection(db, "proposals"), where("siteName", "==", site))
      );

      const rows = snap.docs.map((d) => {
        const data = d.data() || {};
        return {
          proposalId: d.id,
          siteName: data.siteName || "",
          scope: data.scope || "",
          status: data.status || "",
          submittedDate: data.submittedDate || "",
          pdfFileName: data.latestProposalPdfName || "",
          pdfStoragePath: data.latestProposalPdfStoragePath || "",
          pdfDownloadUrl: data.latestProposalPdfUrl || "",
          generatedAtMs: toMillis(data.latestProposalPdfGeneratedAt),
          sizeBytes: 0,
        };
      });

      // Most recently printed first; proposals that were never printed sink to
      // the bottom, where they are shown disabled rather than hidden.
      rows.sort((a, b) => {
        const aHas = a.pdfStoragePath || a.pdfDownloadUrl ? 1 : 0;
        const bHas = b.pdfStoragePath || b.pdfDownloadUrl ? 1 : 0;
        if (aHas !== bHas) return bHas - aHas;
        if (b.generatedAtMs !== a.generatedAtMs) return b.generatedAtMs - a.generatedAtMs;
        return toMillis(b.submittedDate) - toMillis(a.submittedDate);
      });

      setProposalOptions(rows);
      setLoadingProposals(false);

      // File sizes are the practical constraint here — these PDFs are
      // html2canvas rasters with job photos and get large. Fetch them after the
      // list is already on screen so the picker never waits on metadata.
      const withSizes = await Promise.all(
        rows.map(async (row) => {
          if (!row.pdfStoragePath) return row;
          try {
            const meta = await getMetadata(ref(getStorage(), row.pdfStoragePath));
            return { ...row, sizeBytes: meta.size || 0 };
          } catch (err) {
            console.warn("Could not read proposal PDF size:", row.pdfStoragePath, err);
            return row;
          }
        })
      );

      setProposalOptions(withSizes);
    } catch (err) {
      console.error(err);
      setLoadingProposals(false);
      alert("Could not load proposals for this site.");
    }
  };

  const toggleProposalPick = (proposalId) => {
    setProposalPickerIds((prev) =>
      prev.includes(proposalId)
        ? prev.filter((id) => id !== proposalId)
        : [...prev, proposalId]
    );
  };

  // Checked order is kept as the attach order, and the arrows in the attached
  // list let it be changed afterwards.
  const handleConfirmProposalPicks = () => {
    const next = proposalPickerIds
      .map((id) => proposalOptions.find((p) => p.proposalId === id))
      .filter(Boolean)
      .filter((p) => p.pdfStoragePath || p.pdfDownloadUrl)
      .map((p) => ({
        proposalId: p.proposalId,
        siteName: p.siteName || "",
        scope: p.scope || "",
        pdfFileName: p.pdfFileName || "",
        pdfStoragePath: p.pdfStoragePath || "",
        pdfDownloadUrl: p.pdfDownloadUrl || "",
        sizeBytes: p.sizeBytes || 0,
        attachedAt: new Date().toISOString(),
      }));

    setAttachedProposals(next);
    setProposalPickerOpen(false);
    setReportDirty(true);
  };

  const handleRemoveAttachedProposal = (proposalId) => {
    setAttachedProposals((prev) => prev.filter((p) => p.proposalId !== proposalId));
    setReportDirty(true);
  };

  const moveAttachedProposal = (proposalId, direction) => {
    setAttachedProposals((prev) => {
      const index = prev.findIndex((p) => p.proposalId === proposalId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= prev.length) return prev;

      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setReportDirty(true);
  };

  const attachedSizeBytes = useMemo(
    () => attachedProposals.reduce((sum, p) => sum + (p.sizeBytes || 0), 0),
    [attachedProposals]
  );

  const handleStartNewBlankReport = () => {
    setSelectedReport(null);
    setReportValues(DEFAULT_REPORT_VALUES);
    setPdfDownloadUrl("");
    setAttachedProposals([]);
    setEditMode(false);
    setReportDirty(false);
    if (activeStep !== 4) goToStep(4);
  };

  // keepOpen: true keeps the new report on screen (used by Generate PDF).
  // Default keeps the original behavior: save, then clear for the next report.
  const handleCreateReport = async ({ keepOpen = false } = {}) => {
    if (!selectedTemplate?.id) {
      alert("Pick a template first.");
      return null;
    }

    try {
      const counterRef = doc(db, "appCounters", "reports");
const reportNumber = await runTransaction(db, async (transaction) => {
  const snap = await transaction.get(counterRef);
  if (!snap.exists()) {
    transaction.set(counterRef, { lastNumber: 1000 });
    return 1000;
  }
  const next = (snap.data().lastNumber || 1000) + 1;
  transaction.update(counterRef, { lastNumber: next });
  return next;
});

      const payload = {
        templateId: selectedTemplate.id,
        templateKey: selectedTemplate.key || "",
        templateName: selectedTemplate.name || "",
        reportNumber: String(reportNumber),

        siteName: reportValues.siteName || "",
        propertyAddress: reportValues.propertyAddress || "",
        companyName: reportValues.companyName || "",

        pdfDownloadUrl: "",
        pdfStoragePath: "",
        pdfFileName: "",

        attachedProposals,

        values: reportValues,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const reportRef = await addDoc(collection(db, "reports"), payload);
      // serverTimestamp() is a placeholder locally; show a real date instead.
      const now = new Date();
      const created = { id: reportRef.id, ...payload, createdAt: now, updatedAt: now };
      setReports((prev) => [...prev, created]);
      setReportDirty(false);

      if (keepOpen) {
        setSelectedReport(created);
        return created;
      }

      setSelectedReport(null);
      setReportValues(DEFAULT_REPORT_VALUES);
      setPdfDownloadUrl("");
      setEditMode(false);
      alert(`Report #${created.reportNumber} saved. Ready for a new blank report.`);
      return created;
    } catch (err) {
      console.error(err);
      alert("Could not save report.");
      return null;
    }
  };

  const handleUpdateReport = async () => {
    if (!selectedReport?.id) {
      alert("Open a saved report first.");
      return;
    }
  
    try {
      const updatedPayload = {
        siteName: reportValues.siteName || "",
        propertyAddress: reportValues.propertyAddress || "",
        companyName: reportValues.companyName || "",
        attachedProposals,
        values: reportValues,
        updatedAt: serverTimestamp(),
      };
  
      await updateDoc(doc(db, "reports", selectedReport.id), updatedPayload);

      const localPayload = { ...updatedPayload, updatedAt: new Date() };
  
      setReports((prev) =>
        prev.map((r) =>
          r.id === selectedReport.id
            ? { ...r, ...localPayload }
            : r
        )
      );
  
      setSelectedReport((prev) => ({ ...prev, ...localPayload }));
  
      alert("Saved report updated.");
      setReportDirty(false);
    } catch (err) {
      console.error(err);
      alert("Could not update saved report.");
    }
  };

  const handleOpenReport = async (report) => {
    if (report.templateId !== selectedTemplate?.id) {
      if (!(await confirmLeaveDirtyTemplate())) return;
    }

    if (reportDirty && selectedReport?.id !== report.id) {
      const leave = window.confirm(
        "The report on screen has unsaved changes. Open the other report anyway?"
      );
      if (!leave) return;
    }

    goToStep(4);
    setSelectedReport(report);
    setEditMode(false);
    setReportValues({ ...DEFAULT_REPORT_VALUES, ...(report.values || {}) });
    setPdfDownloadUrl(report.pdfDownloadUrl || "");
    setAttachedProposals(
      Array.isArray(report.attachedProposals) ? report.attachedProposals : []
    );
    setReportDirty(false);
  
    const template = templates.find((t) => t.id === report.templateId);

    if (!template) {
      alert(
        `The template for this report ("${report.templateName || "unknown"}") no longer exists. Values are loaded, but there is no PDF to place them on.`
      );
    }
  
    if (template) {
      let refreshedTemplate = { ...template };
  
      if (template.templatePdfStoragePath) {
        try {
          const storage = getStorage();
          const freshUrl = await getDownloadURL(
            ref(storage, template.templatePdfStoragePath)
          );
  
          refreshedTemplate.templatePdfUrl = freshUrl;
  
          await updateDoc(doc(db, "reportTemplates", template.id), {
            templatePdfUrl: freshUrl,
            updatedAt: serverTimestamp(),
          });
          
        } catch (err) {
          console.error("Could not refresh template PDF URL:", err);
        }
      }
  
      if (refreshedTemplate.id !== lastLoadedTemplateIdRef.current) {
        setTemplateDirty(false);
      }
      setSelectedTemplate(refreshedTemplate);
      setSelectedTemplateId(refreshedTemplate.id);
  
      setTemplates((prev) =>
        prev.map((t) => (t.id === refreshedTemplate.id ? refreshedTemplate : t))
      );
    }
  };

  const handleDeleteReport = async (reportId) => {
    const confirmDelete = window.confirm("Delete this saved report?");
    if (!confirmDelete) return;

    try {
      const reportToDelete = reports.find((r) => r.id === reportId);
      const storage = getStorage();

      if (reportToDelete?.pdfStoragePath) {
        try {
          await deleteObject(ref(storage, reportToDelete.pdfStoragePath));
        } catch (storageErr) {
          console.warn("Could not delete PDF from Storage:", storageErr);
        }
      }

      await deleteDoc(doc(db, "reports", reportId));
      setReports((prev) => prev.filter((r) => r.id !== reportId));

      if (selectedReport?.id === reportId) {
        setSelectedReport(null);
        setReportValues(DEFAULT_REPORT_VALUES);
        setPdfDownloadUrl("");
        setAttachedProposals([]);
        setReportDirty(false);
      }

      alert("Report deleted.");
    } catch (err) {
      console.error(err);
      alert("Could not delete report.");
    }
  };

  const openPrintPreview = () => {
    const printable = document.getElementById("printableArea");
    if (!printable) return;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const iDoc = iframe.contentWindow.document;
    const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((node) => node.outerHTML)
      .join("\n");

    iDoc.open();
    iDoc.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Report</title>
          ${styleLinks}
          <style>
            @page { size: Letter; margin: 0.25in; }
            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .no-print { display: none !important; }
            .editor-shell, .editor-sidebar, .editor-toolbar { display: none !important; }
          </style>
        </head>
        <body>${printable.outerHTML}</body>
      </html>
    `);
    iDoc.close();

    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }, 200);
    };
  };

  const handlePrint = async () => {
    try {
      if (!selectedTemplate?.templatePdfUrl && !legacyImagePages.length) {
        alert("Please upload a template PDF first.");
        return;
      }

      if (templateDirty) {
        const saveTemplate = window.confirm(
          "This template has unsaved field changes. Save the template before generating?"
        );
        if (saveTemplate && !(await handleTemplateSave({ silent: true }))) return;
      }

      // Before: saving here cleared the form and stopped, so no PDF was made.
      // Now the new report is saved, stays open, and the PDF is generated.
      let activeReport = selectedReport;
      let justCreated = false;

      if (!activeReport?.id) {
        const shouldSave = window.confirm(
          "This report has not been saved yet. Save it before generating the PDF?\n\n" +
            "OK = Save and generate\nCancel = Generate without saving"
        );
      
        if (shouldSave) {
          activeReport = await handleCreateReport({ keepOpen: true });
          if (!activeReport) return;
          justCreated = true;
        }
      }

      if (activeReport?.id && reportDirty && !justCreated) {
        await updateDoc(doc(db, "reports", activeReport.id), {
          siteName: reportValues.siteName || "",
          propertyAddress: reportValues.propertyAddress || "",
          companyName: reportValues.companyName || "",
          attachedProposals,
          values: reportValues,
          updatedAt: serverTimestamp(),
        });
      
        setReports((prev) =>
          prev.map((r) =>
            r.id === activeReport.id
              ? { ...r, values: reportValues }
              : r
          )
        );
      
        setSelectedReport((prev) => ({
          ...prev,
          values: reportValues,
        }));
      }
  
      let pdfDoc;

      if (selectedTemplate.templatePdfUrl) {
        const existingPdfBytes = await fetch(selectedTemplate.templatePdfUrl).then((res) =>
          res.arrayBuffer()
        );
        pdfDoc = await PDFDocument.load(existingPdfBytes);
      } else {
        // Legacy image-page template (e.g. the built-in WDI report): build a
        // PDF with one Letter-width page per image, keeping each image's own
        // proportions so fields land where they were placed on screen.
        pdfDoc = await PDFDocument.create();
        const LETTER_WIDTH_PT = 612;

        for (const page of legacyImagePages) {
          const imageBytes = await fetch(page.imageUrl).then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status} loading template page`);
            return res.arrayBuffer();
          });
          const head = new Uint8Array(imageBytes.slice(0, 4));
          const isPng = head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e;
          const image = isPng
            ? await pdfDoc.embedPng(imageBytes)
            : await pdfDoc.embedJpg(imageBytes);

          const pageHeight = (LETTER_WIDTH_PT * image.height) / image.width;
          const newPage = pdfDoc.addPage([LETTER_WIDTH_PT, pageHeight]);
          newPage.drawImage(image, {
            x: 0,
            y: 0,
            width: LETTER_WIDTH_PT,
            height: pageHeight,
          });
        }
      }

      const pdfPages = pdfDoc.getPages();
  
      for (const field of selectedTemplate.fields || []) {
        const pdfPage = pdfPages[field.page];
        if (!pdfPage) continue;
  
        const pageSize = pdfPage.getSize();

        // Fields are stored at 100% zoom on an 816px-wide page, and the page is
        // drawn keeping its proportions, so one scale covers both directions.
        // Before, this used the on-screen size at the current zoom and skipped
        // every field on a page that hadn't finished rendering.
        const scale = pageSize.width / BASE_PAGE_WIDTH;
        const scaleX = scale;
        const scaleY = scale;
  
        const value = getDisplayValue(
          field,
          reportValues[field.id],
          allPropertyFieldOptions
        );
  
        const pdfX = field.x * scaleX;
        const pdfY = pageSize.height - ((field.y + field.height) * scaleY);
        const pdfWidth = field.width * scaleX;
        const pdfHeight = field.height * scaleY;
  
        if (field.type === "checkbox") {
          if (reportValues[field.id]) {
            const font = await getEmbeddedFont(
              pdfDoc,
              field.fontFamily,
              field.fontStyle || "regular"
            );
            pdfPage.drawText("X", {
              x: pdfX,
              y: pdfY,
              size: Math.max(10, (field.fontSize || 14) * scaleY),
              font,
              color: hexToRgbPdfLib(field.fontColor || "#111111"),
              characterSpacing: (field.letterSpacing ?? 0) * scaleX,
            });
          }
          continue;
        }
  
        if (field.type === "signature") {
          const sigDataUrl = reportValues[field.id];
          if (sigDataUrl) {
            const pngImage = await pdfDoc.embedPng(sigDataUrl);
            pdfPage.drawImage(pngImage, {
              x: pdfX,
              y: pdfY,
              width: pdfWidth,
              height: pdfHeight,
            });
          }
          continue;
        }
  
        if (!value) continue;
  
        const font = await getEmbeddedFont(
          pdfDoc,
          field.fontFamily,
          field.fontStyle || "regular"
        );
        const fontSize = Math.max(8, (field.fontSize || 14) * scaleY);
        const color = hexToRgbPdfLib(field.fontColor || "#111111");
        const charSpacing = (field.letterSpacing ?? 0) * scaleX;
        const lineStep = fontSize * (field.lineHeight ?? 1.15);
        const align = field.textAlign || "left";

        // Wrap the same way the on-screen box does, then draw line by line.
        const lines = String(value)
          .split("\n")
          .flatMap((para) => wrapLineForPdf(para, font, fontSize, pdfWidth, charSpacing));
  
        lines.forEach((line, idx) => {
          const textWidth =
            font.widthOfTextAtSize(line, fontSize) +
            Math.max(0, line.length - 1) * charSpacing;
        
          let drawX = pdfX;
        
          if (align === "center") {
            drawX = pdfX + (pdfWidth - textWidth) / 2;
          }
        
          if (align === "right") {
            drawX = pdfX + pdfWidth - textWidth;
          }
        
          pdfPage.drawText(line, {
            x: drawX,
            y: pdfY + pdfHeight - fontSize - idx * lineStep,
            size: fontSize,
            font,
            color,
            characterSpacing: charSpacing,
          });
        });
      }
  
      // ---- Append selected proposals -------------------------------------
      // pdf-lib is already holding the finished report, so attaching is just
      // copyPages + addPage before the save. copyPages keeps each source page
      // at its own size, so a Letter proposal behind a non-Letter template
      // produces a mixed-size packet. That prints and views correctly, and
      // rescaling would only soften the html2canvas raster.
      //
      // Fetching is done in a first pass, before anything is added to the
      // document. That way the exhibit divider can only ever list proposals
      // that really made it in — a divider promising "Exhibit B" with no
      // Exhibit B behind it would be worse than no divider at all.
      const failedAttachments = [];
      const loadedAttachments = [];

      for (const attachment of attachedProposals) {
        try {
          // Prefer re-deriving the URL from the storage path, the same way
          // handleOpenReport refreshes template URLs. A URL saved on the
          // report months ago can outlive the token it was minted with.
          let sourceUrl = attachment.pdfDownloadUrl || "";

          if (attachment.pdfStoragePath) {
            try {
              sourceUrl = await getDownloadURL(
                ref(getStorage(), attachment.pdfStoragePath)
              );
            } catch (refreshErr) {
              console.warn(
                "Could not refresh proposal PDF URL, falling back to the saved one:",
                attachment.pdfStoragePath,
                refreshErr
              );
            }
          }

          if (!sourceUrl) throw new Error("No URL for this proposal PDF.");

          const attachmentBytes = await fetch(sourceUrl).then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.arrayBuffer();
          });

          loadedAttachments.push({
            attachment,
            attachmentDoc: await PDFDocument.load(attachmentBytes),
          });
        } catch (attachErr) {
          // One bad attachment must not cost you the whole report. Skip it,
          // keep going, and say which one at the end.
          console.error("Could not attach proposal:", attachment, attachErr);
          failedAttachments.push(
            attachment.scope || attachment.pdfFileName || attachment.proposalId
          );
        }
      }

      // ---- Exhibit divider ------------------------------------------------
      // One page, regardless of how many proposals are attached, listing every
      // exhibit in the order the proposals follow it.
      if (loadedAttachments.length) {
        try {
          await drawExhibitDividerPage(
            pdfDoc,
            loadedAttachments.map((entry) => entry.attachment)
          );
        } catch (dividerErr) {
          // The proposals are the point; the divider is a courtesy. If it
          // fails, still ship the packet rather than losing the whole report.
          console.error("Could not draw the exhibit divider page:", dividerErr);
        }
      }

      for (const { attachmentDoc } of loadedAttachments) {
        const copiedPages = await pdfDoc.copyPages(
          attachmentDoc,
          attachmentDoc.getPageIndices()
        );

        copiedPages.forEach((page) => pdfDoc.addPage(page));
      }

      const finalPdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([finalPdfBytes], { type: "application/pdf" });
  
      const storage = getStorage();
      const filename = `REPORT-${activeReport?.reportNumber || Date.now()}.pdf`;
      const reportId = activeReport?.id || `temp-${Date.now()}`;
      const pdfPath = `reports/${reportId}/pdfs/${filename}`;
      const pdfRef = ref(storage, pdfPath);
  
      await uploadBytes(pdfRef, pdfBlob, { contentType: "application/pdf" });
      const downloadURL = await getDownloadURL(pdfRef);
  
      setPdfDownloadUrl(downloadURL);
      setReportDirty(false);
  
      if (activeReport?.id) {
        const pdfInfo = {
          pdfDownloadUrl: downloadURL,
          pdfStoragePath: pdfPath,
          pdfFileName: filename,
        };

        await updateDoc(doc(db, "reports", activeReport.id), {
          pdfDownloadUrl: downloadURL,
          pdfStoragePath: pdfPath,
          pdfFileName: filename,
          siteName: reportValues.siteName || "",
          propertyAddress: reportValues.propertyAddress || "",
          companyName: reportValues.companyName || "",
          attachedProposals,
          values: reportValues,
          updatedAt: serverTimestamp(),
        });
  
        setSelectedReport((prev) =>
          prev?.id === activeReport.id
            ? { ...prev, ...pdfInfo, values: reportValues, updatedAt: new Date() }
            : prev
        );

        setReports((prev) =>
          prev.map((r) =>
            r.id === activeReport.id
              ? {
                  ...r,
                  pdfDownloadUrl: downloadURL,
                  pdfStoragePath: pdfPath,
                  pdfFileName: filename,
                  siteName: reportValues.siteName || "",
                  propertyAddress: reportValues.propertyAddress || "",
                  companyName: reportValues.companyName || "",
                  attachedProposals,
                  values: reportValues,
                }
              : r
          )
        );
      }
  
      window.open(downloadURL, "_blank", "noopener,noreferrer");

      // The report itself generated fine — say plainly which proposals did not
      // make it in, rather than letting an incomplete packet go out quietly.
      if (failedAttachments.length) {
        alert(
          "The report was generated, but these proposals could not be attached:\n\n" +
            failedAttachments.map((name) => `• ${name}`).join("\n") +
            "\n\nOpen each one in Proposals and click Print/Download to regenerate its PDF, then generate this report again."
        );
      }
    } catch (err) {
      console.error(err);
      alert("PDF generation failed.");
    }
  };

 const memoizedPdfFile = useMemo(() => {
  if (!selectedTemplate?.templatePdfUrl) return null;

  return {
    url: selectedTemplate.templatePdfUrl,
    withCredentials: false,
    disableRange: true,
    disableStream: true,
  };
}, [selectedTemplate?.templatePdfUrl]);

  // One Fill Out the Report input. Unchanged from before — it is only pulled
  // out of the list so each input can be wrapped with its move buttons.
  const renderReportField = (field) => {
  if (field.type === "signature") {
    return (
      <SignaturePadField
        key={field.id}
        label={field.label}
        value={reportValues[field.id] || ""}
        onChange={(sig) => handleReportValueChange(field.id, sig)}
      />
    );
  }

  if (field.type === "checkbox") {
    return (
      <label key={field.id} className="checkbox-row">
        <input
          type="checkbox"
          checked={!!reportValues[field.id]}
          onChange={(e) => handleReportValueChange(field.id, e.target.checked)}
        />
        {field.label}
      </label>
    );
  }

  if (field.type === "textarea") {
    return (
      <div key={field.id} className="field-input-block">
        <label>{field.label}</label>
        <textarea
          rows={3}
          value={reportValues[field.id] || ""}
          onChange={(e) => handleReportValueChange(field.id, e.target.value)}
        />
      </div>
    );
  }

  if (field.type === "site") {
    return (
      <div key={field.id} className="field-input-block">
        <label>{field.label}</label>
        <select
          value={reportValues.siteName || ""}
          onChange={(e) => handleSiteSelection(e.target.value)}
        >
          <option value="">Select Property</option>
          {propertyRecords.map((property) => (
            <option key={property.id} value={property.propertyName || ""}>
              {property.propertyName || ""}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "datePicker") {
    return (
      <div key={field.id} className="field-input-block">
        <label>{field.label}</label>
        <input
          type="date"
          value={reportValues[field.id] || ""}
          onChange={(e) => handleReportValueChange(field.id, e.target.value)}
        />
      </div>
    );
  }

  if (field.type === "dbField") {
    const selectedOption = findSourceOption(allPropertyFieldOptions, field);
  
    const isDateField =
    selectedOption?.type === "date" &&
    /^\d{4}-\d{2}-\d{2}$/.test(reportValues[field.id] || "");
    const isTextareaField = selectedOption?.type === "textarea";
  
    if (isTextareaField) {
      return (
        <div key={field.id} className="field-input-block">
          <label>{field.label}</label>
          <textarea
            rows={3}
            value={reportValues[field.id] || ""}
            onChange={(e) => handleReportValueChange(field.id, e.target.value)}
          />
        </div>
      );
    }
  
    return (
      <div key={field.id} className="field-input-block">
        <label>{field.label}</label>
        <input
          type={isDateField ? "date" : "text"}
          value={reportValues[field.id] || ""}
          onChange={(e) => handleReportValueChange(field.id, e.target.value)}
        />
      </div>
    );
  }

  return (
    <div key={field.id} className="field-input-block">
      <label>{field.label}</label>
      <input
        type={field.type === "date" ? "date" : "text"}
        value={reportValues[field.id] || ""}
        onChange={(e) => handleReportValueChange(field.id, e.target.value)}
      />
    </div>
  );
  };

  const templateReady = !!selectedTemplate?.id;
  const pdfReady = !!selectedTemplate?.templatePdfUrl || legacyImagePages.length > 0;
  const usingLegacyPages = !selectedTemplate?.templatePdfUrl && legacyImagePages.length > 0;

  // Field layer for one page. Fields live at 100% zoom; this layer scales them
  // with the page. Shared by PDF pages and legacy image pages.
  const renderOverlayLayer = (pageIndex) => (
    <div
      className="rpt-overlay-layer"
      style={{
        width: BASE_PAGE_WIDTH,
        transform: `scale(${zoomFactor})`,
      }}
    >
      {(selectedTemplate.fields || [])
        .filter((field) => Number(field.page) === pageIndex)
        .map((field) => (
          <FieldOverlay
            key={field.id}
            field={field}
            value={reportValues[field.id]}
            selected={selectedFieldId === field.id}
            onMouseDown={handleMouseDownField}
            editMode={editMode}
            allPropertyFieldOptions={allPropertyFieldOptions}
          />
        ))}
    </div>
  );
  const fieldCount = (selectedTemplate?.fields || []).length;

  const stepDone = {
    1: templateReady,
    2: pdfReady,
    3: fieldCount > 0 && !templateDirty,
    4: !!selectedReport?.id && !reportDirty,
    5: !!(selectedReport?.pdfDownloadUrl || pdfDownloadUrl) && !reportDirty,
  };

  const stepDisabled = (step) =>
    (step > 1 && !templateReady) || ((step === 3 || step === 4) && !pdfReady);

  const fieldsOffPdf = pdfReady && totalPages
    ? (selectedTemplate?.fields || []).filter((f) => Number(f.page) >= totalPages).length
    : 0;

  return (
    <div className="reports-page">
      {/* ---------- Always visible: which template, and its state ---------- */}
      <div className="rpt-header no-print">
        <div className="rpt-header-picker">
          <label htmlFor="rpt-template-select">Template</label>
          <select
            id="rpt-template-select"
            value={selectedTemplateId}
            onChange={(e) => handleTemplateDropdownChange(e.target.value)}
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.templatePdfUrl ? "" : getLegacyImagePages(t).length ? " (page images)" : " (no PDF yet)"}
                {` — ${(t.fields || []).length} field${(t.fields || []).length === 1 ? "" : "s"}`}
              </option>
            ))}
          </select>
        </div>

        <div className="template-status-row rpt-header-status">
          <span className={pdfReady ? "status-good" : "status-bad"}>
            {usingLegacyPages ? "Page images" : `PDF ${pdfReady ? "uploaded" : "missing"}`}
          </span>
          <span className={fieldCount ? "status-good" : "status-warn"}>
            Fields: {fieldCount}
          </span>
          <span className={selectedReport?.id ? "status-good" : "status-warn"}>
            {selectedReport?.id
              ? `Editing report #${selectedReport.reportNumber || ""}`
              : "New blank report"}
          </span>
          <span className={reportDirty ? "status-warn" : "status-good"}>
            {reportDirty ? "Unsaved report changes" : "Report saved"}
          </span>
        </div>

        <button
          type="button"
          className="blue-button compact-btn"
          onClick={() => setEditMode((v) => !v)}
          title="Field boxes vs. filled-in values"
        >
          {editMode ? "Show filled values" : "Show field boxes"}
        </button>
      </div>

      {templateDirty && (
        <div className="rpt-dirty-bar no-print" role="status">
          <span>
            Unsaved field changes on <strong>{selectedTemplate?.name}</strong>. Save the
            template so these fields load every time you pick it.
          </span>
          <button
            type="button"
            className="green-button compact-btn"
            onClick={() => handleTemplateSave()}
            disabled={savingTemplate}
          >
            {savingTemplate ? "Saving…" : "Save Template"}
          </button>
        </div>
      )}

      {/* ---------- Step strip ---------- */}
      <section className="rpt-guide no-print" ref={wizardRef}>
        <div className="rpt-guide-title">
          <h2>5 Simple Steps</h2>
          <p>Build the template once (1–3). Fill out reports from it any time (4–5).</p>
        </div>
        <div className="rpt-guide-grid">
          {REPORT_STEPS.map(({ step, title, help, color }) => (
            <button
              type="button"
              key={step}
              className={`rpt-guide-step ${color} ${activeStep === step ? "active" : ""}`}
              onClick={() => goToStep(step)}
              disabled={stepDisabled(step)}
              aria-current={activeStep === step ? "step" : undefined}
            >
              <span className="rpt-guide-num">{stepDone[step] ? "✓" : step}</span>
              <strong>{title}</strong>
              <small>{help}</small>
            </button>
          ))}
        </div>
      </section>

      <div className="editor-shell">
        <aside className="editor-sidebar no-print">
          {/* ---------------- STEP 1 ---------------- */}
          {activeStep === 1 && (
            <section className="sidebar-card rpt-step-card step-card-blue" ref={stepCardRef}>
              <div className="rpt-step-heading">
                <span>1</span>
                <div>
                  <h3>Choose a Template</h3>
                  <p>Pick one from the Template dropdown at the top, or create a new one here.</p>
                </div>
              </div>

              {templateReady && (
                <div className="rpt-help">
                  <strong>{selectedTemplate.name}</strong>
                  <br />
                  {pdfReady
                    ? usingLegacyPages
                      ? `Page images: ${totalPages} pages (upload a PDF in Step 2 to switch to PDF)`
                      : `PDF: ${selectedTemplate.templatePdfFileName || "uploaded"} (${totalPages || selectedTemplate.pageCount || 0} pages)`
                    : "No PDF uploaded yet."}
                  <br />
                  {fieldCount} field{fieldCount === 1 ? "" : "s"} placed
                  {selectedTemplate.updatedAt ? ` · last saved ${formatTimestamp(selectedTemplate.updatedAt)}` : ""}
                </div>
              )}

              <div className="toolbar-actions">
                <button className="blue-button compact-btn" onClick={handleRenameTemplate}>
                  Rename
                </button>
                <button className="blue-button compact-btn" onClick={handleDuplicateTemplate}>
                  Duplicate
                </button>
                <button className="red-button compact-btn" onClick={handleDeleteTemplate}>
                  Delete
                </button>
              </div>

              <div className="rpt-subcard">
                <label htmlFor="rpt-new-template">Create a new template</label>
                <input
                  id="rpt-new-template"
                  type="text"
                  placeholder="Example: WDI Inspection Report"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateTemplate();
                  }}
                />
                <button className="green-button compact-btn" onClick={handleCreateTemplate}>
                  Create Template
                </button>
              </div>

              <div className="rpt-step-nav">
                <span />
                <button
                  type="button"
                  className="blue-button compact-btn"
                  disabled={!templateReady}
                  onClick={() => goToStep(pdfReady && fieldCount ? 4 : 2)}
                >
                  {pdfReady && fieldCount ? "Next: Fill Out Report →" : "Next: Upload PDF →"}
                </button>
              </div>
            </section>
          )}

          {/* ---------------- STEP 2 ---------------- */}
          {activeStep === 2 && (
            <section className="sidebar-card rpt-step-card step-card-purple" ref={stepCardRef}>
              <div className="rpt-step-heading">
                <span>2</span>
                <div>
                  <h3>Upload the Template PDF</h3>
                  <p>The blank form the values get printed onto. One PDF, up to {MAX_TEMPLATE_PAGES} pages.</p>
                </div>
              </div>

              {pdfReady && (
                <div className="rpt-help">
                  {usingLegacyPages ? (
                    <>Current template: <strong>page images</strong> (built-in format)</>
                  ) : (
                    <>Current PDF: <strong>{selectedTemplate.templatePdfFileName || "uploaded"}</strong></>
                  )}
                  <br />
                  Pages: {totalPages || selectedTemplate.pageCount || 0}
                  <br />
                  Uploading a new PDF replaces it. Your placed fields are kept.
                </div>
              )}

              <input
                key={pdfInputKey}
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => handleQueueTemplatePdf(e.target.files)}
              />

              {queuedTemplatePdf ? (
                <div className="rpt-queued">Ready to upload: {queuedTemplatePdf.name}</div>
              ) : null}

              <button
                className="green-button"
                onClick={handleUploadTemplatePdf}
                disabled={!queuedTemplatePdf || uploadingPdf}
              >
                {uploadingPdf ? "Uploading…" : "Upload Template PDF"}
              </button>

              <div className="rpt-step-nav">
                <button type="button" className="blue-button compact-btn" onClick={() => goToStep(1)}>
                  ← Back
                </button>
                <button
                  type="button"
                  className="blue-button compact-btn"
                  disabled={!pdfReady}
                  onClick={() => goToStep(3)}
                >
                  Next: Place Fields →
                </button>
              </div>
            </section>
          )}

          {/* ---------------- STEP 3 ---------------- */}
          {activeStep === 3 && (
            <section className="sidebar-card rpt-step-card step-card-green" ref={stepCardRef}>
              <div className="rpt-step-heading">
                <span>3</span>
                <div>
                  <h3>Place Fields</h3>
                  <p>Add a field, drag its blue box onto the PDF, then set what fills it in.</p>
                </div>
              </div>

              <div className="rpt-help green-help">
                <strong>Site</strong> and <strong>Property</strong> fields fill in automatically from the
                property picked in Step 4. Choose which property detail a Property field shows with
                <em> Source Field</em>. Click <strong>Save Template</strong> when you're done — that's
                what makes these fields load every time.
              </div>

              <label className="rpt-mini-label">Add a field to page {currentVisiblePage + 1}</label>
              <div className="field-button-grid">
                <button className="blue-button mini-field-btn" onClick={() => handleAddField("site")}>Site</button>
                <button className="blue-button mini-field-btn" onClick={() => handleAddField("dbField")}>Property</button>
                <button className="blue-button mini-field-btn" onClick={() => handleAddField("datePicker")}>Date</button>
                <button className="blue-button mini-field-btn" onClick={() => handleAddField("text")}>Text</button>
                <button className="blue-button mini-field-btn" onClick={() => handleAddField("textarea")}>Textarea</button>
                <button className="blue-button mini-field-btn" onClick={() => handleAddField("checkbox")}>Checkbox</button>
                <button className="blue-button mini-field-btn" onClick={() => handleAddField("signature")}>Signature</button>
              </div>

              {fieldsOffPdf > 0 && (
                <div className="unsaved-warning">
                  {fieldsOffPdf} field(s) are set to a page this PDF doesn't have. Select them from the list below and change Page.
                </div>
              )}

              {fieldCount > 0 && (
                <details className="rpt-field-list">
                  <summary>All fields ({fieldCount})</summary>
                  {(selectedTemplate.fields || []).map((f) => (
                    <button
                      type="button"
                      key={f.id}
                      className={selectedFieldId === f.id ? "active" : ""}
                      onClick={() => {
                        setSelectedFieldId(f.id);
                        if (Number(f.page) < totalPages) goToPage(Number(f.page) + 1);
                      }}
                    >
                      <span>{f.label || f.type}</span>
                      <small>
                        p{Number(f.page) + 1} · {f.type}
                        {f.type === "dbField" && getSourceCollection(f) === "managementCompanies"
                          ? " · mgmt"
                          : ""}
                      </small>
                    </button>
                  ))}
                </details>
              )}

              <div className="rpt-subcard">
                <h4>Selected Field</h4>
            {selectedField ? (
              
              <>
            
            <label>Label</label>
<input
  value={selectedField.label}
  onChange={(e) => handleFieldPropChange("label", e.target.value)}
/>

<label>Type</label>
<input value={selectedField.type} readOnly />

<label>Page (1st page = 0)</label>
<input
  type="number"
  min="0"
  max={Math.max(0, totalPages - 1)}
  value={selectedField.page}
  onChange={(e) => handleFieldPropChange("page", e.target.value)}
/>

<label>X (at 100%)</label>
<input
  type="number"
  value={selectedField.x}
  onChange={(e) => handleFieldPropChange("x", e.target.value)}
/>

<label>Y (at 100%)</label>
<input
  type="number"
  value={selectedField.y}
  onChange={(e) => handleFieldPropChange("y", e.target.value)}
/>

<label>Width</label>
<input
  type="number"
  value={selectedField.width}
  onChange={(e) => handleFieldPropChange("width", e.target.value)}
/>

<label>Height</label>
<input
  type="number"
  value={selectedField.height}
  onChange={(e) => handleFieldPropChange("height", e.target.value)}
/>

{selectedField.type !== "checkbox" && selectedField.type !== "signature" && (
  <>
    <label>Font Size</label>
    <input
      type="number"
      value={selectedField.fontSize || 14}
      onChange={(e) => handleFieldPropChange("fontSize", e.target.value)}
    />

    <label>Font Family</label>
    <select
      value={selectedField.fontFamily || "Helvetica"}
      onChange={(e) => handleFieldPropChange("fontFamily", e.target.value)}
    >
      <option value="Helvetica">Helvetica</option>
      <option value="TimesRoman">Times Roman</option>
      <option value="Courier">Courier</option>
    </select>

    <label>Font Style</label>
<select
  value={selectedField.fontStyle || "regular"}
  onChange={(e) => handleFieldPropChange("fontStyle", e.target.value)}
>
  <option value="regular">Regular</option>
  <option value="bold">Bold</option>
  <option value="italic">Italic</option>
</select>

<label>Text Align</label>
<select
  value={selectedField.textAlign || "left"}
  onChange={(e) => handleFieldPropChange("textAlign", e.target.value)}
>
  <option value="left">Left</option>
  <option value="center">Center</option>
  <option value="right">Right</option>
</select>

    <label>Font Color</label>
    <input
      type="color"
      value={selectedField.fontColor || "#111111"}
      onChange={(e) => handleFieldPropChange("fontColor", e.target.value)}
    />

<label>Letter Spacing</label>
<input
  type="number"
  step="0.5"
  value={selectedField.letterSpacing ?? 0}
  onChange={(e) => handleFieldPropChange("letterSpacing", e.target.value)}
/>

<label>Line Height</label>
<input
  type="number"
  step="0.05"
  value={selectedField.lineHeight ?? 1.15}
  onChange={(e) => handleFieldPropChange("lineHeight", e.target.value)}
/>
  </>
)}

{selectedField?.type === "dbField" && (
  <>
    <label>Fills in from</label>
    <select
      value={getSourceCollection(selectedField)}
      onChange={(e) => {
        // Changing the collection clears the old field name, which belonged
        // to the other collection.
        handleDbFieldSourceChange(`${e.target.value}:`);
      }}
    >
      {SOURCE_COLLECTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>

    {getSourceCollection(selectedField) === "managementCompanies" ? (
      <p className="rpt-small">
        Read from the management company on the property you pick in Step 4, matched
        by the Management name on that property.
      </p>
    ) : (
      <>
        <label>Lookup By</label>
        <select
          value={selectedField.lookupBy || "propertyName"}
          onChange={(e) => handleFieldPropChange("lookupBy", e.target.value)}
        >
          <option value="propertyName">propertyName</option>
        </select>
      </>
    )}

    <label>Source Field</label>
<select
  value={selectedField.sourceField ? `${getSourceCollection(selectedField)}:${selectedField.sourceField}` : ""}
  onChange={(e) => handleDbFieldSourceChange(e.target.value)}
>
      <option value="">Select field</option>
      {SOURCE_COLLECTIONS.map((source) => {
        const options = allPropertyFieldOptions.filter(
          (opt) => opt.collection === source.value
        );
        if (!options.length) return null;
        return (
          <optgroup key={source.value} label={source.label}>
            {options.map((opt) => (
              <option key={`${opt.collection}:${opt.value}`} value={`${opt.collection}:${opt.value}`}>
                {opt.label}
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>

    {selectedField.sourceField && !findSourceOption(allPropertyFieldOptions, selectedField) && (
      <p className="rpt-small">
        Saved as “{selectedField.sourceField}”, which no record uses right now. It still
        fills in if a record gets that field back.
      </p>
    )}
  </>
)}

                <button className="red-button" onClick={handleDeleteField}>
                  Delete Field
                </button>
              </>
            ) : (
              <p>Select a field box on the page.</p>
            )}
              </div>

              <div className="rpt-step-nav">
                <button type="button" className="blue-button compact-btn" onClick={() => goToStep(2)}>
                  ← Back
                </button>
                <button
                  type="button"
                  className="green-button compact-btn"
                  disabled={savingTemplate}
                  onClick={async () => {
                    if (templateDirty && !(await handleTemplateSave())) return;
                    goToStep(4);
                  }}
                >
                  {templateDirty ? "Save & Next: Fill Out →" : "Next: Fill Out Report →"}
                </button>
              </div>
            </section>
          )}

          {/* ---------------- STEP 4 ---------------- */}
          {activeStep === 4 && (
            <section className="sidebar-card rpt-step-card step-card-orange" ref={stepCardRef}>
              <div className="rpt-step-heading">
                <span>4</span>
                <div>
                  <h3>Fill Out the Report</h3>
                  <p>
                    {selectedReport?.id
                      ? `Editing saved report #${selectedReport.reportNumber || ""}.`
                      : "New blank report. Pick the property first — Site and Property fields fill in for you."}
                  </p>
                </div>
              </div>

              {selectedReport?.updatedAt && (
                <div className="template-meta-info">
                  Last saved: {formatTimestamp(selectedReport.updatedAt)}
                  {selectedReport?.pdfFileName ? ` · PDF: ${selectedReport.pdfFileName}` : ""}
                </div>
              )}


          <div className="field-input-block">
  <label>Internal Notes</label>
  <textarea
    rows={3}
    placeholder="Internal notes only. These do not print on the PDF."
    value={reportValues.internalNotes || ""}
    onChange={(e) => {
      setReportValues((prev) => ({
        ...prev,
        internalNotes: e.target.value,
      }));
      setReportDirty(true);
    }}
    onBlur={() => setReportDirty(true)}
  />
</div>

<div className="field-input-block">
  <label>Report Status</label>
  <select
    value={reportValues.reportStatus || "Draft"}
    onChange={(e) => {
      setReportValues((prev) => ({
        ...prev,
        reportStatus: e.target.value,
      }));
      setReportDirty(true);
    }}
  >
    <option value="Draft">Draft</option>
    <option value="Final">Final</option>
    <option value="Sent">Sent</option>
  </select>
</div>

            <div className="field-input-block">
            <label>Property</label>
  <select
    value={reportValues.siteName || ""}
    onChange={(e) => handleSiteSelection(e.target.value)}
  >
   <option value="">Select Property</option>
   {propertyRecords.map((property) => (
  <option key={property.id} value={property.propertyName || ""}>
    {property.propertyName || ""}
  </option>
))}
  </select>
</div>

<p className="rpt-small">
  Arrows set the order of this list only. Nothing on the PDF moves.
</p>

{orderedFormFields.map((field, formIndex) => {
  const moveControls = (
    <div className="rpt-move-col">
      <button
        type="button"
        className="rpt-move-btn"
        title="Move up"
        aria-label={`Move ${field.label || "field"} up`}
        disabled={formIndex === 0}
        onClick={() => moveFormField(field.id, -1)}
      >
        ▲
      </button>
      <button
        type="button"
        className="rpt-move-btn"
        title="Move down"
        aria-label={`Move ${field.label || "field"} down`}
        disabled={formIndex === orderedFormFields.length - 1}
        onClick={() => moveFormField(field.id, 1)}
      >
        ▼
      </button>
    </div>
  );

  const wrap = (inner) => (
    <div className="rpt-form-row" key={field.id}>
      {moveControls}
      <div className="rpt-form-row-body">{inner}</div>
    </div>
  );

  return wrap(renderReportField(field));
})}



              <div className="toolbar-actions">
                <button
                  className="green-button"
                  onClick={() => (selectedReport?.id ? handleUpdateReport() : handleCreateReport())}
                >
                  {selectedReport?.id ? "Update Saved Report" : "Save New Report"}
                </button>
                <button className="blue-button" onClick={handleStartNewBlankReport}>
                  Start New Blank Report
                </button>
              </div>

              <div className="rpt-step-nav">
                <button type="button" className="blue-button compact-btn" onClick={() => goToStep(3)}>
                  ← Fields
                </button>
                <button type="button" className="blue-button compact-btn" onClick={() => goToStep(5)}>
                  Next: PDF & Saved →
                </button>
              </div>
            </section>
          )}

          {/* ---------------- STEP 5 ---------------- */}
          {activeStep === 5 && (
            <section className="sidebar-card rpt-step-card step-card-teal" ref={stepCardRef}>
              <div className="rpt-step-heading">
                <span>5</span>
                <div>
                  <h3>Generate PDF & Saved Reports</h3>
                  <p>Print the values onto the template, or reopen a report you saved earlier.</p>
                </div>
              </div>

              <div className="rpt-subcard">
                <h4>PDF</h4>


  {reportDirty && selectedReport?.pdfDownloadUrl && (
    <div className="unsaved-warning">
      Report values changed. Regenerate the PDF before sending or downloading.
    </div>
  )}

  {/* Attachments. Deliberately above the Generate button and always showing a
      count, so pages are never appended to an emailed document silently. */}
  <div className="rpt-attach-block">
    <div className="rpt-attach-head">
      <h5>Attached Proposals</h5>
      <button
        type="button"
        className="blue-button compact-btn"
        onClick={handleOpenProposalPicker}
      >
        Add Proposal
        {attachedProposals.length ? ` (${attachedProposals.length})` : ""}
      </button>
    </div>

    {!reportValues.siteName && (
      <p className="rpt-small">
        Pick a site in Step 4 to see proposals for it.
      </p>
    )}

    {reportValues.siteName && !attachedProposals.length && (
      <p className="rpt-small">
        No proposals attached. The PDF will be the report only.
      </p>
    )}

    {attachedProposals.length > 0 && (
      <>
        <p className="rpt-small">
          A single "Exhibits" divider page is added after the report, then these
          proposals follow in this order.
        </p>

        <div className="rpt-attach-list">
          {attachedProposals.map((attachment, index) => (
            <div className="rpt-attach-row" key={attachment.proposalId}>
              <div className="rpt-move-col">
                <button
                  type="button"
                  className="rpt-move-btn"
                  title="Move up"
                  aria-label={`Move ${attachment.scope || "proposal"} up`}
                  disabled={index === 0}
                  onClick={() => moveAttachedProposal(attachment.proposalId, -1)}
                >
                  ▲
                </button>
                <button
                  type="button"
                  className="rpt-move-btn"
                  title="Move down"
                  aria-label={`Move ${attachment.scope || "proposal"} down`}
                  disabled={index === attachedProposals.length - 1}
                  onClick={() => moveAttachedProposal(attachment.proposalId, 1)}
                >
                  ▼
                </button>
              </div>

              <div className="rpt-attach-body">
                <strong>
                  Exhibit {exhibitLetter(index)} – {proposalLabel(attachment)}
                </strong>
                <span className="rpt-attach-meta">
                  {[attachment.siteName, formatBytes(attachment.sizeBytes)]
                    .filter(Boolean)
                    .join(" • ")}
                </span>
              </div>

              <button
                type="button"
                className="red-button compact-btn"
                onClick={() => handleRemoveAttachedProposal(attachment.proposalId)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        {attachedSizeBytes > 0 && (
          <p className="rpt-small">
            Attachments total {formatBytes(attachedSizeBytes)}.
          </p>
        )}

        {attachedSizeBytes > ATTACHMENT_SIZE_WARN_BYTES && (
          <div className="unsaved-warning">
            This packet is about {formatBytes(attachedSizeBytes)} before the report
            itself is added. Many mail servers reject attachments over 20–25MB.
            Consider sending fewer proposals, or sending them as a separate email.
          </div>
        )}
      </>
    )}
  </div>

  <button className="blue-button" onClick={() => handlePrint()}>
    Generate / Download PDF
  </button>

  {selectedReport?.pdfDownloadUrl && (
    <a
      href={selectedReport.pdfDownloadUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="green-button download-link"
      style={{ marginTop: 8 }}
    >
      Open Saved PDF
    </a>
  )}

  {!selectedReport?.pdfDownloadUrl && pdfDownloadUrl && (
    <a
      href={pdfDownloadUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="green-button download-link"
      style={{ marginTop: 8 }}
    >
      Open Generated PDF
    </a>
  )}
              </div>

              <div className="rpt-subcard">
                <h4>Saved Reports</h4>
                <p className="rpt-small">Opening a report loads its template and takes you to Step 4.</p>

            <input
              type="text"
              placeholder="Search by site name, address, report #, or template"
              value={reportSearch}
              onChange={(e) => setReportSearch(e.target.value)}
            />

            <div className="saved-reports-list" style={{ marginTop: 10 }}>
              {filteredReports.map((report) => (
                <div key={report.id} className="saved-report-row">
                  <button
                    className="saved-report-btn"
                    onClick={() => handleOpenReport(report)}
                  >
                    <div className="saved-report-header">
  <strong>
    {report.templateName || "Report"} #{report.reportNumber || ""}
  </strong>

  <span
    className={`report-status-badge ${
      (report.values?.reportStatus || "Draft").toLowerCase()
    }`}
  >
    {report.values?.reportStatus || "Draft"}
  </span>
</div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      {report.siteName || report.values?.siteName || "No Site Name"}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      {report.propertyAddress || report.values?.propertyAddress || ""}
                    </div>
                  </button>

                  <button
                    className="red-button"
                    onClick={() => handleDeleteReport(report.id)}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
              </div>

              <div className="rpt-step-nav">
                <button type="button" className="blue-button compact-btn" onClick={() => goToStep(4)}>
                  ← Back
                </button>
                <span />
              </div>
            </section>
          )}
        </aside>

        <main className="editor-main">
          <div className="rpt-pdf-toolbar no-print">
            <div className="page-jump-row">
              <span>Page</span>
              <input
                type="number"
                min="1"
                max={totalPages || 1}
                value={currentVisiblePage + 1}
                onChange={(e) => goToPage(e.target.value)}
              />
              <span>of {totalPages || 0}</span>
            </div>

            <div className="zoom-row">
              <span>Zoom</span>
              <button
                className="blue-button mini-field-btn"
                onClick={() => setPdfZoom((v) => Math.max(40, v - 10))}
                aria-label="Zoom out"
              >
                -
              </button>
              <input
                type="number"
                min="40"
                max="200"
                value={pdfZoom}
                onChange={(e) =>
                  setPdfZoom(Math.min(200, Math.max(40, Number(e.target.value) || 100)))
                }
              />
              <span>%</span>
              <button
                className="blue-button mini-field-btn"
                onClick={() => setPdfZoom((v) => Math.min(200, v + 10))}
                aria-label="Zoom in"
              >
                +
              </button>
            </div>

            <span className="rpt-mode-note">
              {editMode ? "Showing field boxes" : "Showing filled values"}
            </span>
          </div>

          <div
            ref={pdfScrollRef}
            className="pdf-scroll-box no-print"
            onScroll={handlePdfScroll}
          >
            <div id="printableArea" className="template-preview-stack">
              {pdfReady ? (
                <Document
                  key={`${selectedTemplate?.id || "template"}-${selectedTemplate?.templatePdfUrl || ""}`}
                  file={memoizedPdfFile}
                  onLoadSuccess={({ numPages }) => {
                    setPdfPreviewPageCount(numPages);
                    setSelectedTemplate((prev) => ({
                      ...prev,
                      pageCount: prev.pageCount || numPages,
                    }));
                  }}
                  onLoadError={(err) => {
                    console.error("Template PDF load error:", err);
                  }}
                  loading={<div className="rpt-empty">Loading PDF…</div>}
                  error={
                    <div className="rpt-empty">
                      The template PDF didn't load. Check your connection and reload the page.
                      If it keeps happening, re-upload the PDF in Step 2.
                    </div>
                  }
                >
                  {Array.from(
                    { length: totalPages },
                    (_, index) => index + 1
                  ).map((pageNumber, pageIndex) => (
                    <div
                      key={`pdf-page-${pageNumber}`}
                      className="template-page-print"
                      ref={(el) => {
                        pageRefs.current[pageIndex] = el;
                      }}
                      onMouseDown={() => setSelectedFieldId(null)}
                      style={{ position: "relative", marginBottom: 24 }}
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={(BASE_PAGE_WIDTH * pdfZoom) / 100}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onRenderSuccess={() =>
                          handlePageRenderSuccess(pageIndex, pageRefs.current[pageIndex])
                        }
                      />

                      {/* Fields live at 100% zoom; this layer scales them with the page. */}
                      {renderOverlayLayer(pageIndex)}
                    </div>
                  ))}
                </Document>
              ) : usingLegacyPages ? (
                legacyImagePages.map((page, pageIndex) => (
                  <div
                    key={page.id || `img-page-${pageIndex}`}
                    className="template-page-print"
                    ref={(el) => {
                      pageRefs.current[pageIndex] = el;
                    }}
                    onMouseDown={() => setSelectedFieldId(null)}
                    style={{ position: "relative", marginBottom: 24 }}
                  >
                    <img
                      src={page.imageUrl}
                      alt={`Page ${pageIndex + 1}`}
                      draggable={false}
                      style={{
                        display: "block",
                        width: (BASE_PAGE_WIDTH * pdfZoom) / 100,
                        height: "auto",
                        userSelect: "none",
                      }}
                      onLoad={() =>
                        handlePageRenderSuccess(pageIndex, pageRefs.current[pageIndex])
                      }
                    />
                    {renderOverlayLayer(pageIndex)}
                  </div>
                ))
              ) : (
                <div className="rpt-empty">
                  {templateReady ? (
                    <>
                      No PDF on this template yet.{" "}
                      <button type="button" className="blue-button compact-btn" onClick={() => goToStep(2)}>
                        Go to Step 2: Upload PDF
                      </button>
                    </>
                  ) : (
                    "Create or pick a template in Step 1 to begin."
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* ---------------- Proposal picker ---------------- */}
      {proposalPickerOpen && createPortal(
        <>
          <div
            onClick={() => setProposalPickerOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,.55)",
              zIndex: 999998,
            }}
          />
          <div
            className="rpt-portal rpt-proposal-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(620px, 92vw)",
              maxHeight: "86vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              background: "#fff",
              padding: 16,
              borderRadius: 12,
              boxShadow: "0 18px 44px rgba(15, 23, 42, 0.28)",
              zIndex: 999999,
              boxSizing: "border-box",
            }}
          >
            <div className="rpt-proposal-modal-head">
              <h3>Proposals for {reportValues.siteName || "this site"}</h3>
              <p className="rpt-small">
                Most recently printed first. Checked proposals are appended to the
                end of the report PDF in the order you check them.
              </p>
            </div>

            <div className="rpt-proposal-modal-body">
              {loadingProposals && <p className="rpt-small">Loading proposals…</p>}

              {!loadingProposals && !proposalOptions.length && (
                <p className="rpt-small">
                  No proposals found for this site. Proposals are matched on the
                  exact site name, so check that the proposal uses the same
                  property as this report.
                </p>
              )}

              {!loadingProposals &&
                proposalOptions.map((option) => {
                  const hasPdf = !!(option.pdfStoragePath || option.pdfDownloadUrl);
                  const checked = proposalPickerIds.includes(option.proposalId);

                  return (
                    <label
                      key={option.proposalId}
                      className={`rpt-proposal-option${hasPdf ? "" : " is-disabled"}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!hasPdf}
                        onChange={() => toggleProposalPick(option.proposalId)}
                      />

                      <span className="rpt-proposal-option-body">
                        <strong>{proposalLabel(option)}</strong>

                        <span className="rpt-proposal-meta">
                          {[
                            option.status,
                            option.submittedDate
                              ? `Submitted ${formatDateMMDDYYYY(option.submittedDate)}`
                              : "",
                            option.generatedAtMs
                              ? `PDF ${formatDateMMDDYYYY(
                                  new Date(option.generatedAtMs)
                                )}`
                              : "",
                            formatBytes(option.sizeBytes),
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </span>

                        {/* Never printed proposals are listed but disabled, so a
                            proposal you know exists is not silently missing. */}
                        {!hasPdf && (
                          <span className="rpt-proposal-warning">
                            No PDF yet. Open this proposal in Proposals and click
                            Print/Download, then reopen this list.
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
            </div>

            <div className="rpt-proposal-modal-actions">
              <button
                type="button"
                className="blue-button"
                onClick={() => setProposalPickerIds([])}
                disabled={!proposalPickerIds.length}
              >
                Clear All
              </button>
              <button
                type="button"
                className="red-button"
                onClick={() => setProposalPickerOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="green-button"
                onClick={handleConfirmProposalPicks}
              >
                Attach {proposalPickerIds.length || 0}
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
